/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cdmiller
 */

// import math from 'mathjs';
// import {Dyn} from './dynMiscellany';
// import mediator from './mediatorUtil';

export const wrapDynAttribute = function(Dyn) {
    let mediator = Dyn.mediator;

class Attribute {
    /**
     * Constructor for Attribute class, has name and cases[]
     * @param attribNameString
     * @param attribCaseArray
     * @param attribIndex
     * @param attribCount
     * @param attribID
     * @param {{*}} [metaData] - optional metadata settings for this attribute.
     * @constructor
     */
    constructor( attribNameString, attribCaseArray, attribIndex, attribCount, attribID, metaData ) {

        // initialize parameters, being careful of construction order in cases of dependencies
        this.name = attribNameString; // attribute display name (not necessarily unique)
        this.longName = attribNameString;  // set default for long name
        this.id = attribID; // attribute name adjusted to be unique within its Dyn.dataSet
        this.originalCases = this._rawToNormalizedCases( attribCaseArray );	// sampled, unfiltered, unmodified, original case data
        this.cases = [];						// sampled, filtered, modified case data (e.g. cases removed, values changed type, etc).
        this.population = null;         // {Dyn.populationAttribute} original population data above data is a sample
        this.attribIndex = attribIndex;
        this.attribCount = attribCount;
        this.color = null; // current attribute color, used to create category colors or numeric color gradients
        this.isFixedAttributeColor = false; // true if numeric color gradient fixed by metadata or user override
        this.colorMap = null; // map of fixed category to color mappings, if any
        this.multiAttrStrokeColor = null;
        this.multiAttrFillColor = null;
        this.statsPreFilter = null;     // {Dyn.dataUtil.AttributeStats} after sampling, before filtering (all cases and categories, etc)
        this.stats = null;              // {Dyn.dataUtil.AttributeStats} after sampling and filtering (may have fewer cases)
        this.dataType = undefined; // {Dyn.dataUtil.types|undefined}, use Attribute.stats.isNumeric(), etc, if undefined.
        this.subType = undefined;  // {Dyn.dataUtil.subtypes|undefined}, as determined by this.stats.getNumericSubtype().
        this.isSortAscending = true; // {Boolean} numeric/categorical sort order preference, defaults to ascending
        this.isVisible = true;  // by default its visible, unless marked in metadata
        this.isFiltered = this.isFiltered();
        this.fixedCategories = null; // custom category sort order, including additional categories not found in data.
        this.formula = null; // {Dyn.AttributeFormula|null}
        this.formulaString = "";
        this.isDerived = false;
        if( Dyn.dataUtil.isSpecialAttribute_Case( this )) {
            this.isCaseAttribute = true; // else undefined
        }
        this.format = {}; // user-defined display format, overrides data-defined format in this.stats.numericDecimals

        // we assign an initial color, which is replaced later by the metadata color or call to setAttributeColorScheme()
        this.setAttributeColor( Dyn.colorUtil.hsb_to_PlatformColor(0,0,0));

        // add in optional parameters from attribute metadata
        if( metaData) this.setFromMetaData( metaData );

        // update the initial data type and attribute statistics using the default and optional values above
        // NOTE: we do not evaluate the formula yet, since this attribute may reference attributes not yet constructed.
        this._calcDataTypeAndOrder(); // update 'cases' and stats with optional data type changes
        //console.log( "Attribute()", this.name, this.getDataType(Boolean(this.subType)), ( this.dataType || this.subType )?"nonNative":"");

        // save fixed category colors, first from natural color names in data, overridden by meta-data category colors.
        this.findNaturalColorsForCategories( this.statsPreFilter.categories ); // e.g. "blue" is hex color '#080000'
        if( metaData?.category_color || metaData?.categories ) {
            this.extractColorMapFromMetaData( metaData.category_color, metaData.categories );
        }

        // Preserve original values as constructed, so save() can detect changes to save with plots
        this.original = {
            name: this.name,
            longName: this.longName,
        };
    }

    /**
     * modify this attribute with any optional meta-data settings
     * @param metaData {*}
     */
    setFromMetaData( metaData ) {
        Dyn.assert( metaData );
        // ALL metadata parameters should be optional,
        // Any metadata parameter with an unexpected format should be ignored

        // Set the attribute name
        if( metaData.name && metaData.name.length > 0 ){
            this.name = metaData.name.replace(String.fromCharCode(160)," ");
        }
        // Set the attribute long name
        if( metaData.long_name && metaData.long_name.length > 0 ){
            this.longName = metaData.long_name; // metaData.long_name maps to attribute.longName
        }
        // Set the attribute formula
        if( metaData.formula && metaData.formula.length > 0 ){
            this.setFormula( metaData.formula );
        }
        // set the attribute data type overrides if any
        if( metaData.type || metaData.subtype ) {
            this.setDataType( metaData.type, metaData.subtype );
        }

        // get categorical and numeric sorting of values, if any
        if( metaData.categories ) {
            this.extractCategoryOrderFromMetaData( metaData.categories );
        }
        if( metaData.sort && (typeof metaData.sort === "string") ) {
            // sort descending if asked for by user, otherwise do default ascending sort (of numbers or categories)
            // "sort" keyword value must be "des..." in any case, otherwise sort is ascending.
            this.setSortOrder( metaData.sort );
        }

        // get optional numeric attribute gradient color
        if( metaData.numeric_color && (typeof metaData.numeric_color === "object")) {
            this.setFixedAttributeColorByColorString( metaData.numeric_color.max );
        }
        if( typeof metaData.visibility === 'boolean' ) {
            this.isVisible = metaData.visibility; // don't use this.setVisibility() on construction
        }

        // get attribute case formatting
        if( metaData.numeric_decimals ) {
            var n = metaData.numeric_decimals;
            if( Dyn.mathUtil.isNumber( n ) &&
                Dyn.mathUtil.isInteger( n ) &&
                Dyn.mathUtil.isNonNegative( n )) {
                this.format.decimals = n; // override this.stats.numericDecimals, see getNumericDecimals();
            }
        }
        if( typeof metaData.trailing_zeros === 'boolean' ) {
            // metaData.trailing_zeros, savedData.hasTrailingZeros, both override this.statsPreFilter.hasTrailingZeros
            this.setTrailingZeros( metaData.trailing_zeros );
        }
        if( Number.isFinite( metaData.numeric_interval_width )) {
            this.format.numericIntervalWidth = metaData.numeric_interval_width;
        }
        if( Number.isFinite( metaData.numeric_interval_start )) {
            this.format.numericIntervalStart = metaData.numeric_interval_start;
        }
        if( Number.isFinite( metaData.axisMin)) {
            this.format.axisMin = metaData.axisMin;
        }
        if( Number.isFinite( metaData.axisMax)) {
            this.format.axisMax = metaData.axisMax;
        }
        this.setAxisMajorMinorWidth( metaData.axisMajorWidth, metaData.axisMinorWidth );
        if( metaData.axisFormat && metaData.axisFormat.length > 0 ) {
            this.format.axisFormat = metaData.axisFormat;
        }
    }

    _rawToNormalizedCases( rawCaseValues ) {
        // NOTE: we are removing information about case data here, by converting table strings
        // to numbers; future enhancements may want to check on trailing zeros and other
        // format information that is lost.
        return rawCaseValues.map( str => Dyn.dataUtil.normalizeCaseValueString( str ));
    }

    /** save an attribute for later restore(), all parameters are optional, and not saved if at the default, except ID */
	save() {
		var dataModel = {};

		// save custom data type and subtype if any
		dataModel.ID = this.id;
		if( this.dataType ) { // if empty string or undefined then use default type
			dataModel.dataType = this.dataType; // {Dyn.dataUtil.types|undefined}
		}
		if( this.subType ) { // if empty string or undefined then use default type
			dataModel.subType = this.subType; 	// {Dyn.dataUtil.subtypes|undefined}
		}
        if( this.format.decimals ) {
            dataModel.numericDecimals = this.format.decimals;
        }
        if( typeof this.format.hasTrailingZeros === 'boolean' ) {
            dataModel.hasTrailingZeros = this.format.hasTrailingZeros;
        }
        if( isFinite( this.format.axisMin)) {
            dataModel.axisMin = this.format.axisMin;
        }
        if( isFinite( this.format.axisMax)) {
            dataModel.axisMax = this.format.axisMax;
        }
        if( isFinite( this.format.axisMajorWidth)) {
            dataModel.axisMajorWidth = this.format.axisMajorWidth;
        }
        if( isFinite( this.format.axisMinorWidth)) {
            dataModel.axisMinorWidth = this.format.axisMinorWidth;
        }
        if( this.format.axisFormat ) {
            dataModel.axisFormat = this.format.axisFormat;
        }
        if( this.format.userFormatString ) {
            dataModel.userFormatString = this.format.userFormatString;
            dataModel.userFormatType = this.format.userFormatType;
        }
        if( isFinite( this.format.numericIntervalWidth )) {
            dataModel.numericIntervalWidth = this.format.numericIntervalWidth;
            dataModel.numericIntervalStart = this.format.numericIntervalStart;
        }

        {   // get category data: colors (.colorMap) or name order (.fixedCategories)
            let cats = this.categoriesToMetaData();
            if( cats.hasColors && cats.category_color ) {
                // save category_color values if fixed (else dynamically calculated from this.color)
                dataModel.category_color = cats.category_color;
            }
            if( cats.hasCategories && cats.categories ) {
                // save array of category values (for category order and/or color)
                // (otherwise use alpha order following .isSortAscending value)
                dataModel.categories = cats.categories;
            }
        }

        // save attribute_color value if fixed (else compute numeric gradient max dynamically)
        if( this.color && this.isFixedAttributeColor ) {
            dataModel.attributeColor = this.color.colorString;
        }

        // as of v3.4.0, stop saving dataModel.numeric_decimals, it is already saved above as dataModel.numericDecimals CDM 2025/01/20
        // if( isFinite(this.format.decimals) ){
        //     dataModel.numeric_decimals = this.format.decimals;
        // }

        if( this.isVisible === false ) {
            dataModel.visibility = this.isVisible;
        }

        if( this.isSortAscending === false ){
            dataModel.sort = "descending";
        }
        // save category order if fixed (otherwise use alpha order following .isSortAscending value)
        //if( this.fixedCategories ) {
        //    dataModel.fixedCategories = Dyn.dataUtil.clone( this.fixedCategories );
        //}

        if( this.name !== this.original.name ) { // attribute name has changed
            dataModel.name = this.name;
        }

        if( this.longName !== this.original.longName ) { // attribute description has changed
            dataModel.longName = this.longName;
        }

		return dataModel;
	}

	restore( savedData ) {
        let needsCalcDataTypeAndOrder = false;
		Dyn.assert( savedData.ID === this.id, "Dyn.Attribute.restore() ID mismatch");

		// restore optional parameters, set by this.save() if not at the default
		if( savedData.dataType ) {
            this.setDataType( savedData.dataType, savedData.subType );
            needsCalcDataTypeAndOrder = true;
		}
        if( savedData.numericDecimals ) {
            this.setNumericDecimals( savedData.numericDecimals );
        }
        if( typeof savedData.hasTrailingZeros === "boolean" ) {
            // metaData.trailing_zeros, savedData.hasTrailingZeros, both override this.statsPreFilter.hasTrailingZeros
            this.setTrailingZeros( savedData.hasTrailingZeros );
        }
        if( isFinite( savedData.axisMin)) {
            this.format.axisMin = savedData.axisMin;
        }
        if( isFinite( savedData.axisMax)) {
            this.format.axisMax = savedData.axisMax;
        }
        if( isFinite( savedData.axisMajorWidth)) {
            this.format.axisMajorWidth = savedData.axisMajorWidth;
        }
        if( isFinite( savedData.axisMinorWidth)) {
            this.format.axisMinorWidth = savedData.axisMinorWidth;
        }
        if( savedData.axisFormat && savedData.axisFormat.length > 0 ) {
            this.format.axisFormat = savedData.axisFormat;
        }
        if( savedData.userFormatString ) {
            // note: it is possible userFormatType !== attribute subtype
            this.format.userFormatString = savedData.userFormatString;
            this.format.userFormatType = savedData.userFormatType;
            needsCalcDataTypeAndOrder = true;
        }
        if( savedData.numericIntervalWidth ) {
            this.format.numericIntervalWidth = savedData.numericIntervalWidth;
        }
        if( savedData.numericIntervalStart ) {
            this.format.numericIntervalStart = savedData.numericIntervalStart;
        }
        // To restore category_color
        if( savedData.category_color || savedData.categories ) {
            this.extractColorMapFromMetaData( savedData.category_color, savedData.categories );
        }
        // To set attribute_color value
        if( savedData.attributeColor ) {
            this.setFixedAttributeColorByColorString(savedData.attributeColor);
            Dyn.assert( this.isFixedAttributeColor );
        }
        if( savedData.visibility===false ){
            this.setVisibility( savedData.visibility, false );
        }
        // parameter numeric_decimals is no longer used; it duplicated numericDecimals and was saved from 2017 through 2024 (v3.3.x).
        Dyn.assert( savedData.numeric_decimals === undefined || savedData.numeric_decimals === savedData.numericDecimals )
        // if( isFinite(savedData.numeric_decimals) ){
        //     this.setNumericDecimals(savedData.numeric_decimals);
        // }
        if( savedData.sort && (typeof savedData.sort === "string") )
        {
            // we don't want to call calcDataTypeAndOrder now, if there is
            // datatype change as well it can be handled in one go.
            this.setSortOrder( savedData.sort, false );
            needsCalcDataTypeAndOrder = true;
        }
        // restore custom category order, if any
        if( savedData.categories ) {
            this.extractCategoryOrderFromMetaData( savedData.categories );
            needsCalcDataTypeAndOrder = true;
        }
        if( needsCalcDataTypeAndOrder ){
            // update .cases and .stats with optional data type, sort order, or category order changes.
            this._calcDataTypeAndOrder();
        }
        if( savedData.name ) {
            this.name = savedData.name;
        }
        if( savedData.longName ) {
            this.longName = savedData.longName;
        }
	}

    // Set Name
    setName(name) {
        this.name = name;
    }

    // Set LongName
    setLongName(longName) {
        this.longName = longName;
    }

	/**
	 * Convert a case value or calculated data value to a string for presenting to the user.
	 * Handles rounding, displaying date formats, etc.
	 * Note: Where possible use caseValueToString(), axisValueToString(), which are more specific.
	 * @param v
	 * @param {number} [optionalDecimals] -- 0+, optional decimal digits to display, else use attribute default.
     * @param optionalFormat
     * @returns {string}
     */
	valueToString ( v, optionalDecimals, optionalFormat, forceTrailingZeros=false ) {
		switch( typeof v ) {
            case "number": {
                Dyn.assert( v === Dyn.dataUtil.normalizeCaseValueString(v))
                let subtypes = Dyn.dataUtil.subtypes,
                    decimals = this.getNumericDecimals( optionalDecimals );  // 0+ or undefined
                switch( this.subType ) {
                    // note: to avoid displaying dates in the wrong time zone, all dates without time zone specifiers must be corrected
                    // on input, by Dyn.dataUtil.stringToDateTime().  We don't do the correction here.
                    //		e.g. new Date( Date.parse("2002")).toDateString(); ==> "Mon Dec 31 2001" (one day off, if in GMT +4 time zone)
                    //		e.g. new Date( Date.parse("2002")+Date.parse('1/1/1970')).toDateString(); ==> "Tue Jan 01 2002" (correct year)
                    case subtypes.kDateTime:
                        return Dyn.dataUtil.dateMsToLocalDateTime( v, decimals );
                    case subtypes.kDate:
                        return Dyn.dataUtil.dateMsToLocalDateOnly( v, decimals );
                    case subtypes.kTime:
                        return Dyn.dataUtil.dateMsToLocalTimeOnly( v, decimals );
                    case subtypes.kDurationHMS:
                    case subtypes.kDurationHM:
                        return Dyn.dataUtil.durationSerialToDurationString( v, this.subType, decimals );
                    case subtypes.kMonth:
                        return Dyn.dataUtil.monthSerialToString( v, decimals ); // Note: Month values are 0-11 not DateMS values
                    case subtypes.kMonthYear:
                        return Dyn.dataUtil.monthYearSerialToString( v, decimals, this.format.formatString );
                    case subtypes.kYear:
                        return Dyn.dataUtil.yearSerialToLocalYear( v, decimals );  // Note: Year values are integers, not DateMS values
                    case subtypes.kYearMonth:
                        return Dyn.dataUtil.monthYearSerialToString( v, decimals, this.format.formatString );
                    case subtypes.kDayOfWeek:
                        return Dyn.dataUtil.dayOfWeekSerialToString( v, decimals );
                    case subtypes.kExtractedNumber:
                        return extractedNumberToString( v, decimals, this.format.formatString, forceTrailingZeros || this.hasTrailingZeros() );
                    case subtypes.kFraction: {
                        let fractionFormatString = Dyn.fractionData.mergeFormat(
                            (optionalFormat ? optionalFormat : this.format.formatString), decimals, Number.isFinite( optionalDecimals ));
                        return Dyn.fractionData.numberToFractionString( v, fractionFormatString );
                    }
                }
                // else no subtype
                return numberToString( v, decimals, forceTrailingZeros || this.hasTrailingZeros() );
            }
            case "string":
                // unescaping text to handle <, >, & and single and double quotes
                return Dyn.plotUtil.unescapeText( v );
            default:
                return ""; // for missing values
        }

        function numberToString( v, decimals, useTrailingZeros ) {
            if( Number.isFinite( decimals ) ) {
                // plain number: round to given # digits, remove trailing zeros, add commas before decimal place.
                return Dyn.mathUtil.numericStringWithCommas( Dyn.mathUtil.roundToDecimalPlaces( v, decimals, useTrailingZeros ) );
            }
            return Dyn.mathUtil.numericStringWithCommas( v ); // show full number
        }

        function extractedNumberToString( v, decimals, formatString, useTrailingZeros ) {
            let n = numberToString( v, decimals, useTrailingZeros );
            if( formatString ) {
                // add prefix/suffix, if any, and unescape to handle <, >, & and single and double quotes
                n = Dyn.plotUtil.unescapeText( formatString.replace( Dyn.dataUtil.extractedNumberParam, n ));
            }
            return n;
        }
	}

    /**
     * Make sure a category value has been properly converted to a string
     * @param value - number, string, null/missing value, etc. (see normalizeCaseValueString())
     * @returns {string}
     */
    categoryValueToString( value ) {
        // in call cases, converting non-string values to categories is context dependent; caller should handle
        // all types, including non-finite numbers, string, null/undefined, etc. (see normalizeCaseValueString())
        const type = typeof value,
            result = (type==='string') ? value : "";
        Dyn.assert( typeof value === 'string', "categoryValueToString encountered a non-string: "+type );
        return result;
    }

	/**
	 * Given a case value's index in the attribute, get its formatted display string
	 * Handles date conversions, numeric rounding, etc.
	 * @param caseIndex
	 * @returns {*|string}
	 */
	caseValueToString( caseIndex ) {
		var rawValue;

        if( caseIndex >= this.cases.length ) {
            rawValue = Dyn.dataUtil.kMissingValue;
        } else if( this.subType && this.dataType === Dyn.dataUtil.types.kNumerical ) {
			// if a numeric subtype, show the original value, rather than the formatted value (e.g. show date not date serial number)
			// Note: if the original is a number, it will get converted to the subType like all other numbers.
			rawValue = this.originalCases[ caseIndex ];
		} else {
			rawValue = this.cases[ caseIndex ];
		}
		// convert numbers to strings, fix text, etc.
		return this.valueToString( rawValue );
	}

    /**
     * Get an attribute's case value, with optional formatting. All values are formatted like
     * caseValueToString(), but more flexible -- options can allow raw numbers or decimal precision change.
     * TODO: simplify this function, not all options are used, see getTableRowData() vs getTableRowDataRaw()
     * @param caseIndex
     * @param options
     * @param options.rawNumbers {boolean}-- if true, get numeric values where possible (otherwise strings)
     * @param options.formatted {boolean} -- if false, get raw number, unformatted, for editing or saving
     * @param options.decimals {number} -- decimal precision for numbers
     * @param options.noDerivedData {boolean} -- exclude formula values (use missing value instead
     * @return {string|number} -- formatted string, or number, or empty string if missing value.
     */
    caseValueFormatted( caseIndex, options ) {
        var unFormattedForEditing = ! options.formatted,
            isNumericOriginalFormat = ( this.subType && this.dataType === Dyn.dataUtil.types.kNumerical ),
            isNumericOKInRaw = options.rawNumbers
                && (this.getDataType() === Dyn.dataUtil.types.kNumerical) // natural or forced numeric
                && ! this.subType, // must not be subtype that requires string formatting
            rawValue;
        // get the raw value; some subtypes require getting the original raw value (e.g. date)
        if( this.isDerived && options.noDerivedData ) {
            rawValue = Dyn.dataUtil.kMissingValue;
        } else if( isNumericOriginalFormat || unFormattedForEditing ) {
            rawValue = this.originalCases[caseIndex]; // get '2001-01-31' not date serial number
        } else {
            rawValue = this.cases[ caseIndex ];
        }
        // format the raw value (with option to keep numbers as numbers instead of strings)
        if( unFormattedForEditing ) {
            return rawValue;
        } else if( isNumericOKInRaw && Number.isFinite(rawValue)) {
            let decimals = this.getNumericDecimals( options.decimals );
            return Dyn.mathUtil.roundToDecimalPlaces( rawValue, decimals ); // return number with adjusted precision
        } else {
            return this.valueToString( rawValue, options.decimals ); // return string (for formatting)
        }
    }

    isFormatType( typeString ) {
        switch( typeString ) {
            case 'rawNumbers':
                // case values in this attribute are simple numbers, e.g. -123.456, typeof==='number'
                return this.statsPreFilter.isNumeric() // we actually have numeric case data
                    && (! this.subType); // but not a converted type (e.g. '2001-01-31' converted to serial number)
            // NOTE: reserve for possible future use by Case Card and Case Table.
            // case 'convertedNumbers': {
            //     // case values are converted to numeric serial numbers for calculation, original value can be displayed for formatting
            //     var isForcedNumeric = (this.dataType===Dyn.dataUtil.types.kNumerical),
            //         isDataNumeric = this.statsPreFilter.isNumeric(), // we actually have numeric case data
            //         isNaturalNumeric = isDataNumeric && !(isForcedNumeric || this.subType), // Numeric but this.dataType && this.subType undefined
            //         isConvertedNumeric = isDataNumeric && !(isForcedNumeric || isNaturalNumeric);
            //     return isConvertedNumeric;
            // }
            default:
                return false;
        }
    }

	/** convert numeric value to the given precision but don't (yet) convert to a string. */
	numericAxisValueToPrecision( v, optionalDecimals ) {
		"use strict";
		let fixedDecimals = this.getNumericDecimals( optionalDecimals );
		return Dyn.mathUtil.roundIfInteger( Dyn.mathUtil.roundToDecimalPlaces( v, fixedDecimals ));
	}

	/**
	 * Convert an arbitrary, calculated value on a numeric axis to its display string.
	 * Used for reference lines, tick marks, mean, median, etc.
	 * @param {number|string|undefined} v -- data value
	 * @param {number} [optionalDecimals] -- custom decimal precision (or default to attribute precision + 2 places).
	 * @returns {string}
	 */
	axisValueToString( v, optionalDecimals ) {
        const forceTrailingZeros = true; // axis values always have trailing zeros
		if( Number.isFinite(v) ) {
            const isUnspecifiedPrecision = !Number.isFinite( optionalDecimals ),
                  isFraction = this.subType === Dyn.dataUtil.subtypes.kFraction;
            if( isFraction && isUnspecifiedPrecision ) {
                // use full precision for fraction attributes w/o precision, so 1/3 becomes '1/3', not '333/1000'
            } else {
                if( isUnspecifiedPrecision ) {
                    // if no precision specified, use attribute precision + 2 decimal places
                    optionalDecimals = this.getNumericDecimals() + Dyn.plotUtil.kCalculatedAxisValueExtraPrecision;
                }
                v = this.numericAxisValueToPrecision( v, optionalDecimals );
            }
		}
		return this.valueToString( v, optionalDecimals, undefined, forceTrailingZeros );
	}

	/**
	 * Convert an arbitrary, calculated value on a numeric axis to its display string.
	 * WITH special conversion for relative dates, for example, datetime 2000: "2 seconds".
	 * Used for "Inter-quartile Range" of boxplot, "Standard Deviation" display.
	 * @param {number|string|undefined} v -- data value
	 * @param {number} [optionalDecimals] -- override of Dyn.Attribute.stats.numericDecimals, if applicable.
	 * @returns {string}
	 */
	axisValueToStringRelativeUnits( v, optionalDecimals  ) {
		const subtypes = Dyn.dataUtil.subtypes;
		if( typeof v === "number" && this.subType ) {
			if( typeof optionalDecimals !== "number" ) {
				// if no precision specified, use attribute precision + 2 decimal places.
				optionalDecimals = this.getNumericDecimals() + Dyn.plotUtil.kCalculatedAxisValueExtraPrecision;
			}
			switch (this.subType) {
                case subtypes.kDateTime:
                case subtypes.kDate:
                case subtypes.kTime:
                    return Dyn.DateDataType.serialNumberToRelativeUnit( v, this, optionalDecimals  );
                case subtypes.kYear:
                    return Dyn.YearDataType.serialNumberToRelativeUnit( v, this, optionalDecimals );
                case subtypes.kMonth:
                    return Dyn.loc( "{{n}} months", { n: this.numericAxisValueToPrecision( v, optionalDecimals ).toString() } );
                case subtypes.kMonthYear:
                    return Dyn.MonthYearDataType.serialNumberToRelativeUnit( v, this, optionalDecimals, this.format.formatString );
                case subtypes.kYearMonth:
                    return Dyn.MonthYearDataType.serialNumberToRelativeUnit( v, this, optionalDecimals, this.format.formatString );
                case subtypes.kDayOfWeek:
                    return Dyn.loc( "{{n}} days", { n: this.numericAxisValueToPrecision( v, optionalDecimals ).toString() } );
                // use default conversion in axisValueToString() for these types:
                //  case subtypes.kDayOfWeek:
                //  case subtypes.kDurationHMS:
                //  case subtypes.kDurationHM:
                //  case subtypes.kFraction
            }
		}
		return this.axisValueToString( v, optionalDecimals );
	}

    /** given user data input, convert to a number or string compatible with the data type of this attribute */
    axisInputToAxisValue( v, dataFormat, isRelativeUnits=false ) {
        let attrType = this.getDataType(),
            axisValue = Dyn.dataUtil.normalizeCaseValueString( v ), // convert string to number or missing value?
            ok = true;
        if( attrType===Dyn.dataUtil.types.kCategorical ) {
            // make sure axis value is a non-empty string
            ok = ! Dyn.dataUtil.isMissing( axisValue );
            axisValue = ''+v;
            Dyn.assert( axisValue?.length > 0 || !ok );
        } else {
            // make sure axis value is a number (of the correct type if any)
            let dataType = Dyn.dataUtil.getDataType( axisValue, dataFormat, attrType, this.subType );
            if( dataType === 'number' && dataFormat.subtype ) {
                const validSubtypes = [ this.subType, Dyn.dataUtil.subtypes.kYear, Dyn.dataUtil.subtypes.kExtractedNumber ];
                if( isRelativeUnits ) {
                    // convert relative units to date ms or month-year serial number
                    switch( this.subType ) {
                        case Dyn.dataUtil.subtypes.kDateTime:
                        case Dyn.dataUtil.subtypes.kDate:
                        case Dyn.dataUtil.subtypes.kTime:
                            axisValue = Dyn.DateDataType.relativeUnitToSerialNumber( v, dataFormat.subtype );
                            ok = Number.isFinite(axisValue);
                            break;
                        case Dyn.dataUtil.subtypes.kMonth:
                        case Dyn.dataUtil.subtypes.kMonthYear:
                        case Dyn.dataUtil.subtypes.kYearMonth:
                            axisValue = Dyn.MonthYearDataType.relativeUnitToSerialNumber( v, dataFormat.subtype );
                            ok = Number.isFinite(axisValue);
                            break;
                        case Dyn.dataUtil.subtypes.kYear:
                            axisValue = Dyn.YearDataType.relativeUnitToSerialNumber( v, dataFormat.subtype );
                            ok = Number.isFinite(axisValue);
                            break;
                        case Dyn.dataUtil.subtypes.kFraction:
                        case Dyn.dataUtil.subtypes.kDurationHM:
                        case Dyn.dataUtil.subtypes.kDurationHMS:
                        default:
                            axisValue = dataFormat.value;
                            ok = validSubtypes.includes( dataFormat.subtype );
                    }
                    Dyn.debugging && console.log("axisInputToAxisValue: relative units ", v, " ==> ", axisValue, " ok=", ok, dataFormat.subtype );
                } else {
                    axisValue = dataFormat.value;
                    ok = validSubtypes.includes( dataFormat.subtype );
                }
            }
            ok = ok && Number.isFinite(axisValue);
            Dyn.assert( Number.isFinite(axisValue) || !ok );
        }
        return ok ? axisValue : Dyn.dataUtil.kMissingValue;
    }

    /**
     * Get the number of decimals (0+) to display, to round numeric values to a certain number of significant digits
     * @param optionalDecimals { number || undefined } -- to override the attribute's calculated number of decimals
     * @param ignoreUserOverride { boolean || undefined } -- optional parameter, if true, ignore user specified precision
     * @returns { number || undefined } -- 0+ digits or undefined
     */
    getNumericDecimals( optionalDecimals=undefined, ignoreUserOverride=false ) {
	    var d = optionalDecimals; // caller-supplied number, if any
	    if( Number.isFinite( d )) return d;

        d = this.format.decimals; // else user-specified number, if any
        if( Number.isFinite( d ) && ! ignoreUserOverride) return d;

        // disable see #2637
        // d = this.format.formulaDecimals; // else formula-calculated number, if any
        // if( isFinite( d )) return d;

        d = this.statsPreFilter.numericDecimals; // else number of decimals found in original data
        return d;
    }

    /**
     * Set the number of decimals to display (e.g. by attribute settings panel),
     * to override the decimal places found in the data. See getNumericDecimals().
     * @param decimalPlaces
     */
    setNumericDecimals( decimalPlaces ) {
        // console.log("setNumericDecimals ",decimalPlaces)
        this.format.decimals = decimalPlaces;
    }

    getFormatString( optionalFormatString ) {
        let formatString = optionalFormatString || this.format.formatString;
        if( this.subType === this.format.userFormatType ) {
            // user override of format string only if data type matches
            formatString = this.format.userFormatString;
        }
        return formatString;
    }

    /** Set the format string (e.g. by attribute settings panel), to override the format found in the data */
    setFormatString( formatString ) {
        //console.log("Attribute.setFormatString ", formatString, this.subType );
        // save the string and data type pair for save/restore
        this.format.userFormatString = formatString;
        this.format.userFormatType = this.subType;
        // override the format string for use by valueToString(), like _calcDataTypeAndOrder()
        this.format.formatString = this.getFormatString();
    }

    /** get the display option to show trailing zeros in data, up to the getNumericDecimals() places. */
    hasTrailingZeros() {
        // check for this.format.hasTrailingZeros, like this.format.decimals from the Attribute meta-data settings
        // NOTE: this.stats.hasTrailingZeros is always false, don't use that value,
        Dyn.debugDataInput && Dyn.assert( this.stats?.hasTrailingZeros === false, "attribute.stats.hasTrailingZeros is always false" );
        return (typeof this.format.hasTrailingZeros === "boolean")
            ? this.format.hasTrailingZeros // use metaData setting
                : this.statsPreFilter.hasTrailingZeros; // else use Numeric:extractedNumber setting
    }

    /**
     * Set the display option to show trailing zeros in data, up to the getNumericDecimals() places.
     * Can be set from metaData.trailing_zeros, savedData.hasTrailingZeros, both override this.statsPreFilter.hasTrailingZeros
     * used by valueToString(); see also axisValueToString() for use of similar parameter forceTrailingZeros.
     * @param hasTrailingZeros
     */
    setTrailingZeros( hasTrailingZeros ) {
        Dyn.assert( typeof hasTrailingZeros === 'boolean', "setTrailingZeros() requires a boolean value" );
        this.format.hasTrailingZeros = hasTrailingZeros;
    }

    /** get current datatype of the attribute.
     * Terminology: Natural Numeric: values are all numbers, e.g. 1, 10.3, 12
     *              Converted Numeric: values auto-converted, e.g. "1,000", "12 inches", or "Mon", "Tues"
     *              Forced Numeric: values forced to numeric by user datatype change.
     * @param wantSubTypeAlso
     * @returns {string}
     */
	getDataType( wantSubTypeAlso ) {
        let result;
		if ( this.isNumeric() ){
			// attribute is naturally numeric (1+ numeric values) without any data type conversions
			// or attribute is converted to numeric with 1+ numeric values
			// or attribute is forced numeric but has 0 numeric values
			// NOTE: use pre-filter stats, because filters should not change the data type
            result = Dyn.dataUtil.types.kNumerical;
		} else {
			// else forced categorical attribute
			// or natural categorical attribute
			// or attribute with all missing values (treat as categorical)
            result = Dyn.dataUtil.types.kCategorical;
		}
        if( wantSubTypeAlso && this.subType) {
            result = result + ":" + this.subType;
        }
        return result;
	}

    /**
     * Return true if the attribute is numeric, either naturally or forced, after use of setDataType() and _calcDataTypeAndOrder().
     * All attributes that are not numeric are categorical, by definition, even if all cases are missing -- see getDataType().
     * @returns {boolean|*}
     */
    isNumeric() {
        return (this.dataType===Dyn.dataUtil.types.kNumerical) // forced numerical?
                || this.statsPreFilter.isNumeric(); // naturally numeric?
    }

	/**
	 * Set the data type and subtype and recalculate stats
	 * @param newDataType {Dyn.dataUtil.types|undefined} -- user specified data type if any
	 * @param [newSubType] {Dyn.dataUtil.subtypes|undefined} -- user specified data type if any
     * @param updateStatsAndCasesNow {boolean} - if true, recalculate statistics now.
	 */
	setDataType( newDataType, newSubType, updateStatsAndCasesNow ) {
        const  oldType = this.dataType,
            oldSubType = this.subType;

        if( Dyn.dataUtil.typesArray.includes( newDataType )) {
            this.dataType = newDataType; // force the data type
        } else {
            this.dataType = undefined; // use this.statsPreFilter.isNumeric(), etc. to determine natural data type
        }
        if( Dyn.dataUtil.subtypesArray.includes( newSubType )) {
            this.subType = newSubType;  // known subtype
        } else {
            this.subType = undefined;   // no subtype, just categorical or numerical
        }
        Dyn.assert( Dyn.dataUtil.isSubtypeValidForType( this.type, this.dataType ),
            "type and subtype mismatch: "+this.type+":"+this.subtype );
        if(( newDataType !== oldType || newSubType !== oldSubType ) && updateStatsAndCasesNow ) {
            this._calcDataTypeAndOrder();   // update stats and case values
        }
    }

    /**
     * Set the attributes sorting/display order, default is ascending.
     * affects the numeric axis order, and the order of category displays.
     * @param sortOrder {string} - sort order string
     * @param updateStatsAndCasesNow {boolean} - if true, recalculate statistics now.
     */
    setSortOrder( sortOrder, updateStatsAndCasesNow ) {
        var oldSort = this.isSortAscending;

        if( sortOrder.substring( 0, 3 ) === "des" ) {
            this.isSortAscending = false;
        } else {
            this.isSortAscending = true;
        }
        if( oldSort !== this.isSortAscending && updateStatsAndCasesNow ) {
            this._calcDataTypeAndOrder();   // update stats and case values
        }
    }

    /*
     * Setting the visibility of the attribute, based on the selection
     * from the case card.
     * by default it would come from Case Card, but in case of metadata and
     * restore event it would require to update casecard
     */
    setVisibility( visibilityStatus, fromCaseCard ) {
        var currentVisibility = this.isVisible;
        if( currentVisibility !== visibilityStatus ){
            this.isVisible = visibilityStatus;
            // If visibility is set through metadata, or restore function
            if (!fromCaseCard){
                // As of now, it doesn't matter which attribute it is - so {}
                mediator.publish( "card:reinstateCaseCard", {} );
            }
        }
    }

    /**
     * Set or clear a formula for an attribute.
     * @param formulaString - formula string, or an empty string to clear the formula
     * @param updateStatsAndCasesNow {boolean} - if true, recalculate statistics and case values now.
     * @param updateIfCleared {boolean} - if true, recalculate statistics and case values now only if attribute cleared.
     * @returns {boolean} -- true if formula added, removed, or updated.
     */
    setFormula( formulaString, updateStatsAndCasesNow, updateIfCleared ) {
        var oldFormulaString = this.formula ? this.formula.formulaString : "",
            newFormulaString = formulaString.trim(), // eliminate whitespace
            isEmptyFormula = newFormulaString.length===0,
            isClearedFormula = !!(isEmptyFormula && oldFormulaString.length),
            formulaChanged = oldFormulaString !== newFormulaString;

        if( formulaChanged ) {
            if ( isEmptyFormula ) {
                this.formula = null;
            } else {
                this.formula = new Dyn.AttributeFormula( newFormulaString );
            }
            if ( updateStatsAndCasesNow || (updateIfCleared && isClearedFormula)) {
                this.calcCasesFromFormula();   // update stats and case values
            }
            // set isDerived if this attribute is depending on a formula, otherwise clear
            this.formulaString = newFormulaString;
            this.isDerived     = !isEmptyFormula;
        }
        if( this.isDerived ) {
            Dyn.assert( this.formula && ! this.formula.isEmptyFormula() ); // has formula and non-empty string
        } else {
            Dyn.assert( this.formula === null && this.formulaString.length === 0 ); // no formula or formula string
        }
        return formulaChanged;
    }

    /**
     * Update the values of 1 or more cases of a user-editable attribute (not-derived, non-case attribute)
     * @param firstCaseIndex
     * @param newValuesArray
     * @returns {boolean}
     */
    setCaseValues( firstCaseIndex, newValuesArray ) {
        Dyn.assert( ! this.isDerivedFromSampling(), "Cases are not editable in Sampling mode");
        Dyn.assert( firstCaseIndex >= 0, "setCaseValue: invalid case index" );
        Dyn.assert( this.isEditable(), "setCaseValue not allowed on formula or Case attributes");
        let changed = false;
        if( this.isEditable() ) {
            // fill with missing cases, between the last existing case and the first new case
            for( let i=this.originalCases.length;i<firstCaseIndex; i++ ) {
                this.originalCases[i] = Dyn.dataUtil.kMissingValue;
                changed = true;
            }
            // normalized and add/update case values
            newValuesArray.forEach( (v,i) => {
                let caseValue = Dyn.dataUtil.normalizeCaseValueString( v );
                if( caseValue !== this.originalCases[ firstCaseIndex + i ] ) {
                    if( Dyn.colorUtil.isColorName( caseValue )) { // get color name ignoring case and removing spaces
                        this.setColorMapForCategory( caseValue, caseValue );
                    }
                    // console.log("Attribute.setCaseValues", this.name, "case", firstCaseIndex+i, this.originalCases[firstCaseIndex + i], "changed to", caseValue );
                    this.originalCases[firstCaseIndex + i] = caseValue;
                    changed = true;
                }
                // else console.log("Attribute.setCaseValues", this.name, "case", firstCaseIndex+i, this.originalCases[firstCaseIndex + i], "UNCHANGED");
            });
            if( changed ){
                let debugOldType = this.getDataType( true );
                this._calcDataTypeAndOrder();   // update stats and case values
                // console.log( "Attribute.setCaseValues data type", this.name, ( debugOldType !== this.getDataType( true ))
                //     ? "CHANGED from "+debugOldType+" to: "+this.getDataType(true)
                //     : "unchanged: "+ this.getDataType(true));
            }
        }
        return changed;
    }

    /**
     * Splice to insert or delete case values from the attribute.
     * @param firstCaseIndex
     * @param deleteCount
     * @param newValuesArray
     * @returns {*|null} - returns array of deleted values, or empty array if nothing deleted, or null if no change.
     */
    spliceCaseValues( firstCaseIndex, deleteCount, ...newValuesArray ) {
        Dyn.assert( ! this.isDerivedFromSampling(), "spliceCaseValues: Cases are not editable in Sampling mode");
        Dyn.assert( firstCaseIndex >= 0, "spliceCaseValues: invalid case index" );
        Dyn.assert( this.isEditable(), "spliceCaseValues not allowed on formula or Case attributes");
        if( this.isEditable()) {
            let oldLength = this.originalCases.length,
                deletedCases = this.originalCases.splice( firstCaseIndex, deleteCount, ...newValuesArray );
            if( this.originalCases.length !== oldLength ){ // if changed
                // let debugOldType = this.getDataType( true );
                this._calcDataTypeAndOrder();   // update stats and case values
                // console.log( "Attribute.spliceCaseValues data type", this.name, "cases:", this.originalCases.length,
                //     ( debugOldType !== this.getDataType( true ))
                //     ? "CHANGED from "+debugOldType+" to: "+this.getDataType(true)
                //     : "unchanged: "+ this.getDataType(true));
                return deletedCases;
            }
        }
        return null;
    }

    /**
     * Change the number of pre-filter cases in this attribute,
     * by adding missing values, or removing values from the end.
     * See also Dyn.dataSet.getNumCasesPreFilter().
     * Caller must notify client objects of change.
     * @param newNumCases - integer 0+
     * @returns {boolean} - true if number of cases changed
     */
    setNumCases( newNumCases ) {
        Dyn.assert( Dyn.mathUtil.isInteger( newNumCases ) && newNumCases >= 0 );
        Dyn.assert( ! this.isDerivedFromSampling(), "Cases are not editable in Sampling mode");
        let oldNumCases = this.originalCases.length;
        if( newNumCases >= 0 ) { // shorten or lengthen the pre-filter cases
            this.originalCases.length = newNumCases; // shorten or lengthen array
        }
        if( newNumCases > oldNumCases ) { // if new cases, change to missing values
            this.originalCases.fill( Dyn.dataUtil.kMissingValue, oldNumCases, newNumCases );
            // if special "Cases" attribute, update case index; formula attributes are updated by caller
            if( this.isCaseAttribute ) {
                for( let i=oldNumCases; i<newNumCases; i++ ) {
                    this.originalCases[i] = i+1;
                }
            }
        }
        if( newNumCases !== oldNumCases ) {
            this._calcDataTypeAndOrder();   // update stats and case values
        }
        return( newNumCases !== oldNumCases );
    }

    /**
     * Update the dependent parts of the attribute after a change to:
     *      this.formula
     * @private
     */
    calcCasesFromFormula() {
        var dataSet = Dyn.dataSet;
        if( this.formula ) {
            // compile the formula
            var scope = this.formula.findDependencies( dataSet, this );
            this.formula.calcAggregates( dataSet, scope );
            this.formula.evalCountStart();

            // update the 'original cases'
            this.originalCases = []; // forget old values
			var i =0, numCases = Dyn.dataSet.getNumCasesPreFilter();
            //console.log( "calcCasesFromFormula() for "+this.id+" "+numCases+" cases.");
            for( ; i<numCases; i++ ) {
                this.formula.getCaseScope( scope, i, dataSet );
                this.originalCases[i] = this.formula.evaluate( scope );
            }
            if( this.formula.error.evaluate ) console.log("formula evaluate error", this.formula.error.evaluate, this.name );
            if( this.formula.evalErrorCount() ) console.log("formula evaluate error count", this.formula.evalErrorCount(), this.formula.evalCount(), this.name );

            // disable, we now determine format just like other case data; issue #2637
            // var formulaFormat = this.formula.getValueFormat( dataSet );
            // this.format.formulaDecimals = formulaFormat.formulaDecimals; // update

            // update the post-filter, post-data-type cases
            this._calcDataTypeAndOrder();
        }
    }

	/**
     * Update the dependent parts of the attribute after a change to:
     *      this.dataType, or
     *      this.subType, or
     *      this.isSortAscending
     * Updates:
     *      this.cases -- initialize or update
     *      this.subType -- if this.dataType===undefined, this.subType might change to the "natural" (numeric) subtype
     *      this.statsPreFilter -- stats for all cases, e.g. data type and categories for all cases (e.g. for legend)
     *      this.stats -- stats for post-filter cases, e.g. actual numeric range, actual number of categories in data.
     * @private
     */
    _calcDataTypeAndOrder() {
        let caseFormat = {},
            caseType;

        // Step 1: get pre-filter stats (all cases, original values) to get natural and final data type
        let wantPreConversion = this.hasNumericIntervalsDataType();
        this.statsPreFilter = Dyn.dataUtil.calcStats( this.originalCases, this.getStatsOptions( true /* pre-filter */, wantPreConversion ) );
        this.subType = this.subType || this.statsPreFilter.getNumericSubtype(); // if no fixed subtype, use the natural subtype if any, else undefined
        this.format.formatString = this.getFormatString( this.statsPreFilter.getFormatString( this.subType ));
        //console.log( "_calcDataTypeAndOrder statsPreFilter", this.statsPreFilter, this.originalCases );

        // Step 2: transform unmodified case values to match final type, if needed
        this.cases = this.originalCases.map( caseValue => {
			caseType = Dyn.dataUtil.getDataType( caseValue, caseFormat, this.dataType, this.subType ); // forced conversions might happen here
            //console.log( "case", i, caseValue, "is type", ""+caseType+":"+caseFormat.subtype, caseFormat.value, "with data type", ""+this.dataType+":"+this.subType);
			if( this.isNumeric()) {
				if( caseFormat.subtype ) {
                    caseValue = caseFormat.value; // string to number (e.g. date serial number/extracted number in string)
				} else if( caseType === "string" ) {
                    caseValue = Dyn.dataUtil.kMissingValue;
				}
			} else {
                if( caseType === "number") {
                    caseValue = caseValue.toString(); // number to string (to match string categories)
                }
			}
            return caseValue;
        });
        //console.log( "_calcDataTypeAndOrder cases post-conversion", this.statsPreFilter, this.cases );

        // Step 3: if categorical:numeric intervals data type, further conversion
        if( this.hasNumericIntervalsDataType()) {
            // Step 3.a: compute numeric intervals and convert cases from natural to intervals data
            this.setNumericIntervals( true, this.format.numericIntervalWidth, this.format.numericIntervalStart ); // set temporary numeric intervals from pre-conversion numeric data range
            if( this.hasNumericIntervals()) { // if intervals actually found (e.g. non-empty numeric range),
                // Step 3.b: convert numbers to interval strings (categories), so pre-filter stats are correct.
                this.cases = this.cases.map( caseValue => {
                    caseType = Dyn.dataUtil.getDataType( caseValue, caseFormat, this.dataType, this.subType );
                    if( caseType === "number") { // if finite numeric value
                        caseValue = this.numericIntervalIndexToString( Dyn.dataUtil.calcIntervalIndex( this.numericIntervals.values, caseValue ));
                    }   // categories and missing values ignored for now
                    return caseValue;
                });
                //console.log( "_calcDataTypeAndOrder step 3.b, numeric intervals", this.cases );
            }
            // Step 3.d: re-calculate statsPreFilter using categorical data and intervals as categories
            this.statsPreFilter = Dyn.dataUtil.calcStats( this.cases, this.getStatsOptions( true /* pre-filter */ ));
            this.setNumericIntervals( false ); // clear temporary numeric intervals
            //console.log( "_calcDataTypeAndOrder step 3.c", this.statsPreFilter );
        }

        // Step 4: get post-filter stats, after now that cases are in their final (converted) data type.
        //      NOTE: .stats of numerical subtypes vary from .statsPreFilter, particularly in decimals and formatting,
        //      because we are not using the original strings to calculate these stats. In general, we want to use
        //      .statsPreFilter for formatting consistency no matter what the filter setting is.
        this.stats = Dyn.dataUtil.calcStats( this.cases, this.getStatsOptions( /* post-filter */ ));
        if( Dyn.debugDataInput ) console.log( "Attribute", '"'+this.name+'" is now', this.dataType?"Forced":"Natural",
            this.getDataType(true), "decimals:", this.statsPreFilter.numericDecimals, "format:", this.format, "range:", this.stats.min, this.stats.max );
    }

	/**
     * Set or clear numeric intervals for multi-case section plots (Pie), or numeric grouping on axis, etc.
     * NOTE: these create TEMPORARY intervals, which may be temporary for a plot (Pie)
     * or for a data type change (this.subType === Dyn.dataUtil.subtypes.kNumericIntervals)
     * @param wantIntervals {boolean}
     * @return {boolean} - true if numeric intervals are in use.
     */
    setNumericIntervals( wantIntervals, optionalWidth, optionalStart ) {
        if( this.statsPreFilter.isNumeric() && wantIntervals ) {
            // create or update numeric intervals, they are 'outside' of data (first tick <= data <= last tick), see Dyn.EIntervalRange
            let range       = new Dyn.mathUtil.Range( this.statsPreFilter.min, this.statsPreFilter.max ),
                numIntervals    = (this.numericIntervals ? this.numericIntervals.numIntervals : Dyn.plotUtil.kStartingNumIntervals),
                intervalOptions = { irLowerOutside:true, irUpperBeyond:true };
            range.makeNonZeroWidth(); // in case all values are missing or equal
            numIntervals = Dyn.mathUtil.clipToMinMax( numIntervals, Dyn.plotUtil.kMinNumIntervals, Dyn.plotUtil.kMaxNumIntervals );
            if( optionalWidth ) {
                // #533 prevent excessive numbers of categories (limit to about 10x typical smallest number of intervals)
                // min bin width is [ 10 x range/(number of cases) ],[ 10 x range / smallest interval created by decimal places]
                // for bin widths > 1, use [100 x range/(number of cases) ], since decimal places does not apply (#1326)
                let isDecimalWidth = (optionalWidth < 1),
                    maxCases = (this.pop ? this.pop : this).originalCases.length * (isDecimalWidth ? 10 : 100),
                    minIntervalWidthByCases = Math.pow( 10, Math.floor( Math.log10( range.width() / maxCases ))), // to nearest power of 10, e.g. 579 -> 100
                    minIntervalByDecimalPlaces = Dyn.mathUtil.decimalPlacesToIntervalWidth( (this.pop ? this.pop : this).statsPreFilter.numericDecimals + 1),
                    minIntervalWidthSanityCheck = isDecimalWidth ?
                        Math.min( minIntervalWidthByCases, minIntervalByDecimalPlaces ) :
                        Math.max( minIntervalWidthByCases, 1.0 );
                intervalOptions.exactWidth = Math.max( optionalWidth, minIntervalWidthSanityCheck );
            }
            if( optionalStart ) {
                range.min = Math.min( range.min, optionalStart);
                intervalOptions.irLowerOutside = false;
                intervalOptions.irLowerExact = true; // start intervals exactly at specified lower end of range
            }
            intervalOptions.isAtMin = this.numericIntervals?.isAtMin;
            intervalOptions.isAtMax = this.numericIntervals?.isAtMax;
            this.numericIntervals = Dyn.dataUtil.calcNiceIntervals( range.min, range.max, numIntervals, intervalOptions );
            // note: we keep these categories separate from this.stats.categories because they are calculated at very different times.
            this.numericIntervals.categories = this._calcNumericIntervalStrings();

            // save as defaults for the next conversion
            this.setNumericIntervalParameters( this.numericIntervals.intervalWidth, this.numericIntervals.min, false /* don't update cases now */ );
            // publish message for case card to set intervalOptions.exactWidth
            mediator.publish( "records:numericIntervalsCalculated", {
                "attributeID": this.id,
                "numericIntervalWidth": this.numericIntervals.intervalWidth,
                "numericIntervalStart": this.numericIntervals.min
            });
        } else {
            this.numericIntervals = null;
        }
        return this.hasNumericIntervals();
    }

    /** is there a temporary numeric interval set (e.g. for Pie or Bar chart?) */
    hasNumericIntervals() {
        return Boolean( this.numericIntervals );
    }

    /** is the data type forced to numeric intervals? (compare to hasNumericIntervals()) */
    hasNumericIntervalsDataType() {
        return this.dataType===Dyn.dataUtil.types.kCategorical && this.subType===Dyn.dataUtil.subtypes.kNumericIntervals;
    }

    /**
     * Change the numeric interval parameters, to override the defaults for data type "categorical:NumericIntervals".
     * Note: these parameters don't affect the plot-specific temporary numeric intervals stored in this.numericIntervals.
     * and note that these parameters can be set even if the change in data type has not happened yet.
     * @param intervalWidth
     * @param intervalStart
     * @returns {boolean} true if this changes the Attribute parameters.
     */
    setNumericIntervalParameters( intervalWidth, intervalStart, updateNow = true) {
        // validate and check for significant change to the parameters
        let validIntervalWidth = Dyn.mathUtil.isPositive( intervalWidth ) ? intervalWidth : undefined,
            validIntervalStart = Dyn.mathUtil.isFinite( intervalStart ) ? intervalStart : undefined,
            isChanged = ! ((validIntervalWidth === this.format.numericIntervalWidth) &&
                           (validIntervalStart === this.format.numericIntervalStart));
        if( isChanged ) {
            // now actually parameters, now that we know change is validated
            this.format.numericIntervalWidth = validIntervalWidth;
            this.format.numericIntervalStart = validIntervalStart;
            if ( updateNow ) {
                this._calcDataTypeAndOrder();
            }
        }
        return isChanged;
    }

    /**
     * Calculate strings for the numeric intervals,
     *   NOTE: this.numericIntervals.values are always in ascending order,
     *   BUT return value for this.numericIntervals.categories are ascending or descending following this.isSortedAscending
     * @return {[]} -- array of categories
     * @private
     */
    _calcNumericIntervalStrings() {
        let originalSubType = this.subType,
            naturalSubType = this.statsPreFilter.getNumericSubtype(), // Dyn.dataUtil.subtype.kDateTime, etc. or undefined if no subtype
            groups = [];
        this.subType = naturalSubType; // format strings using natural subtype
        {
            const width = this.numericIntervals.intervalWidth,
                g = Dyn.dataUtil.getGapForIntervalWidth( this.getNumericDecimals(), width );
            let min = this.numericIntervals.min,
                i = 0;
            while( this.numericIntervals.numIntervals > i++ ) {
                groups.push( this.numericIntervalToString( min, width, g.gap, g.decimals ) );
                min += width;
            }
            // reverse the array if attribute.isSortAscending === false, must reverse all indices as well
            if( this.isSortAscending === false ) {
                groups.reverse();
            }
        }
        this.subType = originalSubType; // restore original subtype
        return groups;
    }

	/**
	 * Convert a numeric interval to a nice string, using a small "gap" between intervals for readability.
     * Assumes intervals created with Dyn.dataUtil.calcNiceIntervals(), to be compatible with Dyn.dataUtil.calcIntervalIndex()
     * e.g. (0, 3, 1) => "0-2", (3, 3, 1) => "3-5", (6, 3, 1) => "6-8".
     * e.g. (0, 3, 0.1) => "0-2.9", (3, 3, 0.1) => "3-5.9", (6, 3, 0.1) => "6-8.9".
     * @param minOfInterval {number}
     * @param widthOfInterval {number}
     * @param gapBetweenIntervals {number}
     * @param decimals {number|undefined} - optional number of decimals to use, else use getNumericDecimals()
     * @returns {string}
     */
    numericIntervalToString( minOfInterval, widthOfInterval, gapBetweenIntervals, decimals ) {
        // NOTE: axisValueToString() uses this.subType to format numeric subtypes like "year" correctly;
        // in the case of subtype=kNumericIntervals, this.subType might need to an override to format year.
        var v1 = minOfInterval,
            v2 = Dyn.mathUtil.roundToPrecision( v1 + widthOfInterval - gapBetweenIntervals ),
            s1 = this.axisValueToString( v1, decimals ),
            s2 = this.axisValueToString( v2, decimals ),
            isNonZeroWidth = ! Dyn.mathUtil.isClose( v1 , v2 );
            if( ! this.isSortAscending ) {
                let temp=s1; s1=s2; s2=temp; // swap
            }
        // notes on formatting:
        //      negative numbers will be -2 to -1.1, -1 to -0.1, 0 to 0.9, 1 to 1.9
        // unlike TinkerPlots we make sure the last interval has the same width as other intervals, otherwise use:
        //      if( (gap < 1 ) && (this.numericIntervals.numIntervals <= i) ) {
        //           gap = 0; // last interval has no gap, e.g. 0-0.9, 1-1.9, 2-3, to match Dyn.dataUtil.calcIntervalIndex()
        //}
        return isNonZeroWidth ? (""+s1+" to "+s2) : s1;
    }

    /**
     * Get the numeric interval string for the case, if there are numeric intervals set
     * E.g. case 1 might have age==6, which would be in category "0-9".
     * @param caseIndex - valid case index or -1 for missing value
     * @returns {string}
     */
    getNumericIntervalString( caseIndex ) {
        var value = this.cases[ caseIndex],
            index;
        if( this.numericIntervals ) {
            // if numeric intervals in sorting attribute, convert case value to interval group
            index = Dyn.dataUtil.calcIntervalIndex( this.numericIntervals.values, value );
            if( this.isSortAscending===false )
                index = this.numericIntervals.numIntervals - index - 1;
            Dyn.assert( index >= -1 && index < this.numericIntervals.categories.length, "array index out of bounds" );
            value = (index >= 0) ? this.numericIntervals.categories[ index ] : Dyn.dataUtil.kMissingValue;
        }
        return value;
    }

    /** from a case index used in a numeric interval, find the numeric interval value (for coloring the case) */
    caseIndexToNumericIntervalValue( caseIndex ) {
        var value = this.cases[ caseIndex],
            index;
        if( this.numericIntervals ) {
            // if numeric intervals in sorting attribute, convert case value to interval group
            index = Dyn.dataUtil.calcIntervalIndex( this.numericIntervals.values, value );
            if( index >= 0 ) {
                value = this.numericIntervalIndexToValue( index );
            } else {
                value = Dyn.dataUtil.kNumericMissingValue;
            }
        }
        return value;
    }

    /**
     * From the index of a numeric interval for this attribute, get the value used to color the interval category
     * @param intervalIndex
     * @returns {number}
     */
    numericIntervalIndexToValue( intervalIndex ) {
        Dyn.assert( this.numericIntervals.numIntervals === this.numericIntervals.categories.length );
        let i = this.isSortAscending ? intervalIndex : this.numericIntervals.numIntervals - intervalIndex - 1,
            stats = this.statsPreFilter,
            value = stats.min + ((stats.max - stats.min )*((i + 0.5)/(1+this.numericIntervals.numIntervals)));
        Dyn.assert( this.numericIntervals, "no intervals defined");
        Dyn.assertArrayIndex( this.numericIntervals.categories, i );
        Dyn.assert( Dyn.mathUtil.isInRange( value, stats.min, stats.max, "out of range getNumerIntervalMidpoint" ));
        return value;
    }

    numericIntervalIndexToString( intervalIndex ) {
        Dyn.assert( this.numericIntervals, "no intervals defined");
        Dyn.assert( this.numericIntervals.numIntervals === this.numericIntervals.categories.length );
        let i = this.isSortAscending ? intervalIndex : this.numericIntervals.numIntervals - intervalIndex - 1;
        return(( i >= 0 ) ? this.numericIntervals.categories[ i ] : Dyn.dataUtil.kMissingValue );
    }

    /**
     * set the attribute to a TEMPORARY state of having ordered numeric categories.
     * See also setNumericIntervals() which sets a similar temporary state.
     */
    setOrderedNumeric( wantOrderedNumeric ) {
        let statsOptions = this.getStatsOptions( /* post-filter */ );
        if( this.statsPreFilter.isNumeric() && this.stats.isNumeric() && wantOrderedNumeric ) {
            // create categories for the formatted numbers (dates, currency, etc)
            let categories = [],
                categoryToValueMap = {},
                filterOutFn = statsOptions.filterFn || (() => false);
            this.cases.forEach( (caseValue, caseIndex) => {
                if( Number.isFinite( caseValue ) && ! filterOutFn( caseIndex )) {
                    let catStr = this.valueToString( caseValue );
                    if( ! categories.includes(catStr)) {
                        categories.push( catStr );
                        categoryToValueMap[catStr] = caseValue;
                    }
                }
            });
            // sort categories according to value, save with updated stats
            categories.sort(( a, b ) => (categoryToValueMap[a] - categoryToValueMap[b]));
            this.stats.setOrderedNumericCategories( categories, categoryToValueMap );
        } else if( this.statsPreFilter.isNumeric() && this.stats.isCategorical()) {
            // undo custom stats
            this.stats = Dyn.dataUtil.calcStats( this.cases, statsOptions );
        }
    }

	/** return true if this attribute is a numeric attribute that wants to be formatted as a date or time. */
	// hasDateTimeType() {
	// 	var subtypes = Dyn.dataUtil.subtypes;
	// 	switch( this.subType ) {
    //         case subtypes.kDateTime:
    //         case subtypes.kDate:
    //         case subtypes.kTime:
    //         case subtypes.kDurationHMS:
    //         case subtypes.kDurationHM:
    //         case subtypes.kMonth:
    //         case subtypes.kMonthYear:
    //         case subtypes.kYear:
    //         case subtypes.kYearMonth:
    //         case subtypes.kDayOfWeek:
    //             return true;
    //         default:
    //             return false;
    //     }
	// }

	/**
	 * @returns {boolean} --true if the data set has a case filter referencing this attribute.
	 */
	isFiltered() {
		"use strict";
		return Dyn.dataSet.caseFilter.getAttributeFilter( this.id ) !== null;
	}

	/**
	 * Check to see if the Dyn.DataSet filter/missing value state has changed since we last calculated stats.
	 * If changed, recalculate the statistics to change the list of categories, numeric range, etc.
	 */
	updateStatsIfDataChange() {
		"use strict";
		var tChanged = false,
			statsOptions = this.getStatsOptions( false /* post-filter */);

		if( this.stats.filterID !== statsOptions.filterID ) {
			this.stats = Dyn.dataUtil.calcStats( this.cases, statsOptions );
			tChanged = true;
		}
		return tChanged;
	}

	/**
	 * Get options necessary for calcStats().
	 * @param isPreFilter - if true, ignore case filters
     * @param isPreConversion - if true, ignore data type conversions
	 */
	getStatsOptions( isPreFilter=false, isPreConversion=false ) {
		var dataSet = Dyn.dataSet,
            isNumericIntervalsType = this.subType === Dyn.dataUtil.subtypes.kNumericIntervals,
			statsOptions = {};

        statsOptions.name = this.name; // for debugging only
		statsOptions.dataType = this.dataType;
		//statsOptions.dataSubType = this.subType;
		statsOptions.isSortAscending = this.isSortAscending;
		statsOptions.fixedCategories = this.fixedCategories;
		statsOptions.attribIndex = this.attribIndex;
		if( isPreConversion ) {
            // use natural data type to get pre-conversion stats, so we know how many numeric or categorical cases there are
            statsOptions.dataType = undefined;
            //statsOptions.dataSubType = this.subType;
        } else if ( isPreFilter ) {
		    // don't set filters, to use all cases
            // data types might cause categorical or numeric data to be treated as a special numeric type, e.g. datetime
            if( isNumericIntervalsType && this.hasNumericIntervals() ) {
                statsOptions.fixedCategories = this.numericIntervals.categories; // save category order with stats
                statsOptions.sparseCategories = true; // remove unused categories in numeric intervals #1326
            }
        } else {
		    // set up filters to get stats only on cases that match the filter
			statsOptions.attrFilter = dataSet.caseFilter.getAttributeFilter( this.id ); // for filtering categories
			statsOptions.filterFn = dataSet.getStatsFilterFunction(); // for filtering cases
			statsOptions.filterID = dataSet.getStatsFilterID();
			if( isNumericIntervalsType ) {
                statsOptions.fixedCategories = this.statsPreFilter.categories; // preserve numerical order #1222
            } else {
                statsOptions.dataType = this.getDataType(); // make sure filtering does not change data type #2251
            }
		}
		return statsOptions;
	}

    /** copy the metadata category order, if any, to fix the sort order or add blank categories */
    extractCategoryOrderFromMetaData( categories ) {
        if( categories && Array.isArray( categories ) && categories.length > 0 ) {
            this.fixedCategories = [];
            categories.forEach( c => {
                if( typeof c === 'string' ) {
                    // if element is a bare string, treat as a category name
                    this.fixedCategories.push( c );
                } else if( typeof c==='object' && typeof c.name=='string' ) {
                    // if element is an object, get category name, ignore other parameters
                    this.fixedCategories.push( c.name );
                }
            });
        }
    }

    /**
     * Copy the fixed categories and colors for categories of this attribute, if any, for saving.
     * inverse functions are extractCategoryOrderFromMetaData() and extractColorMapFromMetaData()
     */
    categoriesToMetaData() {
        let data = {
            categories:[],
            category_color:{},
            hasCategories: false,
            hasColors: false
        };

        // copy fixed categories array if any
        if( this.fixedCategories ) {
            data.categories = Dyn.dataUtil.clone( this.fixedCategories );
        }
        // copy color map if any
        if( this.colorMap  ) {
            for( const [key, value] of Object.entries( this.colorMap )) {
                let ci = data.categories.indexOf( key );
                if( ci >= 0 ) {
                    // replace array element with object {name,c}
                    data.categories[ci] = {
                        name: key,
                        c: value.colorString
                    };
                    data.hasCategories = true;
                } else {
                    // not in array, instead save as category_color mapping
                    data.category_color = data.category_color || {};
                    data.category_color[key] = value.colorString;
                    data.hasColors = true;
                }
            }
        }
        return data;
    }

	/**
     * Extract and validate our list of custom color values for each category.
     * Note: the category values are not checked against the attribute categories; if any do not match they are ignored,
     * and if any categories are not in the color list, the usual color calculations apply.
     * @param category_color -- map of color name to color string e.g. { girls:'green', boys:'yellow' }
     * @categories -- array of category parameters, [ {name:'girls',c:'green'}, {name:'boys',c:'yellow'} ]
     * @returns {*} -- null or { "category":"color", ... } lookup table.
     */
    extractColorMapFromMetaData( category_color, categories ) {
        // colors can be in category_color map
        if( category_color && typeof category_color==='object') {
            for( const [key, value] of Object.entries(category_color)) {
                this.setColorMapForCategory( key, value );
            }
        }
        // or colors can be in category array
        if( categories && Array.isArray( categories )) {
            categories.forEach( c => {
                if( typeof c==='object' && typeof c.c=='string' ) {
                    // if element is an object, get category name, ignore other parameters
                    this.setColorMapForCategory( c.name, c.c );
                }
            });
        }
    }

    /** get data from colorMap suitable for later restore by extractColorMapFromMetaData() */
    colorMapToMetaData() {
        let category_color = {};
        for( let category in this.colorMap ) {
            if( this.colorMap.hasOwnProperty( category )) {
                category_color[category] = this.colorMap[category].colorString;
            }
        }
        return category_color;
    }

    /**
     * Set natural colors for categories that match SVG color names.
     * E.g. 'green' or 'Green': '#008000'
     * @param categoryArray
     */
    findNaturalColorsForCategories( categoryArray ) {
        categoryArray.forEach( (value, index) => {
            if(Dyn.colorUtil.isColorName( value )) { // get color name ignoring case and removing spaces
                this.setColorMapForCategory( value, value );
            }
        });
    }

    /**
     * Set a custom color for the given category of this attribute.
     * Fixes both the category value and the color if needed.
     * @param categoryValue -- required category value, converted to a non-empty string or ignored
     * @param color -- optional color value like "blue" or "#678" or "#060708".
     */
    setColorMapForCategory(categoryValue, color /*, colorMetaData */) {
        // if String or Number, convert to our standard color object, e.g. "female":"#00ff00".
        var normalizedCategory = Dyn.dataUtil.normalizeCaseValueString( categoryValue );
        if( ! Dyn.dataUtil.isMissing( normalizedCategory )) {
            // if((!color) && colorMetaData) {
            //     color = colorMetaData[ normalizedCategory ];
            // }
            this.colorMap = this.colorMap || {};
            this.colorMap[normalizedCategory] =
                Dyn.colorUtil.svgColorString_to_PlatformColor(color);
        }
    }

    /**
     * Set the attribute color which is the basis for a numeric gradient (if numeric attribute data),
     * or the category colors (if categorical). If this is a value fixed by metaData or user color pick,
     * marke it as fixed so dynamic changes to the number of attributes or colors don't change it further.
     * @param newColor - color with colorString, e.g. from Dyn.colorUtil.hsb_to_PlatformColor
     * @param [isFixed]
     */
    setAttributeColor( newColor, isFixed ) {
        Dyn.assert( typeof newColor.colorString === 'string' );
        this.color = newColor;
        this.multiAttrStrokeColor   = Dyn.colorUtil.calcMultiAttributeColor( this.color, this.attribIndex, this.attribCount, false );
        this.multiAttrFillColor     = Dyn.colorUtil.calcMultiAttributeColor( this.color, this.attribIndex, this.attribCount, true );
        this.isFixedAttributeColor = Boolean( isFixed );
        // console.log( "attribute colors for "+this.name, this.color, this.multiAttrStrokeColor, this.multiAttrFillColor );
    }

    /**
     * Set Attribute color value using html hex string or other SVG color, e.g. "#112233".  This both changes
     * the color once ana "fixes" it, preventing dynamic color changes by setAttributeColorScheme().
     */
    setFixedAttributeColorByColorString( colorHex ) {
        // To update color of numerical attributeObj
        if( colorHex && (typeof colorHex === "string")) {
            var attributeColor = Dyn.colorUtil.svgColorString_to_PlatformColor( colorHex );
            this.setAttributeColor( attributeColor, true );
        }
    }

    /**
     * Move the given category of this attribute from the old position to the new position,
     * relative to the current attribute filter. This requires that the category positions
     * be fixed, overriding the MetaData category order, if any, or the dynamic (alphabetical order).
     * @param oldCategoryIndex - current (old) category position, post-filter
     * @param newCategoryIndex - new position of the category, post-filter
     */
    moveOneCategory( oldCategoryIndex, newCategoryIndex ) {

        function repositionInArray( array, oldIndex, newIndex, debugMessage ) {
            // reposition one item in the given array
            let minIndex = Math.min( oldIndex, newIndex ),
                maxIndex = Math.max( oldIndex, newIndex );
            if( minIndex >= 0 && maxIndex < array.length && oldIndex !== newIndex ) {
                // remove item from old position, insert into new position
                let deleted = array.splice( oldIndex, 1 );
                array.splice( newIndex, 0, deleted[0] );
                return true; // success
            }
            Dyn.assert( false, "Attribute.repositionCategory() failed for: "+debugMessage ); // report failure
            return false; // failure
        }

        let success = false;
        if( this.stats.isCategorical()) {
            Dyn.assert( this.statsPreFilter.isCategorical());
            let categoryName = this.stats.categories[oldCategoryIndex],
                oldCatIndexPreFilter = this.statsPreFilter.categories.indexOf( categoryName ),
                newCatIndexPreFilter = this.statsPreFilter.categories.indexOf(
                    this.stats.categories[newCategoryIndex] );
            //console.log( categoryName, oldCatIndexPreFilter, newCatIndexPreFilter );

            // make fixed categories if needed and reposition
            if( ! this.fixedCategories ) {
                this.fixedCategories = Dyn.dataUtil.clone( this.statsPreFilter.categories );
            } else {
                // add missing categories, if any
                for( let i=this.fixedCategories.length, j=this.statsPreFilter.categories.length; i<j; i++ ) {
                    this.fixedCategories.push( this.statsPreFilter.categories[i] );
                }
                Dyn.assert( this.fixedCategories.length >= this.statsPreFilter.categories.length );
            }
            success = repositionInArray( this.fixedCategories, oldCatIndexPreFilter, newCatIndexPreFilter, "fixedCategories" );

            // fix the attribute colors, before statsPreFilter category order is changed, so color moves with category
            if( success ) {
                this.fixedCategories.forEach( (category) => {
                    let color = Dyn.colorUtil.calcCategoryColor( this.statsPreFilter, this.color, category, false,
                        this.colorMap, this.isSortAscending ).colorString;
                    this.setColorMapForCategory( category, color );
                });
            }

            // reposition in pre-filter stats to match
            if( success ) {
                success = repositionInArray( this.statsPreFilter.categories, oldCatIndexPreFilter, newCatIndexPreFilter, "statsPreFilter.categories" );
            }

            // reposition in post-filter stats to match
            if( success ) {
                success = repositionInArray( this.stats.categories, oldCategoryIndex, newCategoryIndex, "stats.categories" );
                Dyn.assert( success && categoryName === this.stats.categories[newCategoryIndex], "Failure: category did not move to new position");
            }
        } else if( this.hasNumericIntervals()) {
            // reposition the temporary categories formed by intervals for Bar or Pie charts; these are not saved or re-colored
            // Note: this is unused code; if we want to be able to drag temporary or forced numeric intervals, the new order should be saved separately,
            // and used on recalc of numeric intervals (recalculated with plot), and cleared when interval parameters changed or intervals
            // no longer used.  --CDM 08/05/2022
            success = repositionInArray( this.numericIntervals.categories, oldCategoryIndex, newCategoryIndex, "numericIntervals.categories" );
        }
        return success;
    }

    /**
     * Update the attribute color where there is a change in color scheme or number of attributes.
     * This affects the attributes which have not been set with a fixed color.
     * See setAttributeColor() for details.
     */
    setAttributeColorScheme( attributeIndex, attributeCount ) {
        this.attribIndex = attributeIndex; // save the parameters used for dynamic colors
        this.attribCount = attributeCount;
        if( ! this.isFixedAttributeColor ) { // if this attribute has a dynamic color, change it.
            this.setAttributeColor( Dyn.colorUtil.calcAttributeColor( this.attribIndex, this.attribCount ) );
        }
    }

    /** Set the major and minor axis tick widths for plots using this attribute.
     * NOTE: normally undefined, so widths are dynamically calculated by Dyn.plotUtil.calcNiceIntervals()
     * @param axisMajorWidth {number} - major tick width in data units, > 0, or undefined to clear
     * @param axisMinorWidth {number} - minor tick width in data units, >= 0, or undefined to clear
     */
    setAxisMajorMinorWidth( axisMajorWidth, axisMinorWidth ) {
        if( Dyn.mathUtil.isPositive( axisMajorWidth )) {
            this.format.axisMajorWidth = axisMajorWidth; // defined numeric and > 0
        } else {
            delete this.format.axisMajorWidth; // else clear; use dynamic calculation
        }
        if( Dyn.mathUtil.isNonNegative( axisMinorWidth )) { // defined numeric and >= 0, 0 means no minor ticks
            this.format.axisMinorWidth = axisMinorWidth;
        } else {
            delete this.format.axisMinorWidth;
        }
    }

    // return true if this is an ordinary editable attribute that has values that can be changed
    // (not the "Case" attribute, not a formula attribute);
    isEditable() {
        return ! (this.isDerived || this.isCaseAttribute);
    }

    /**
     * Is this attribute a sample of the original attribute?
     * True in sampling mode when viewing Samples, and this attribute's cases have been modified.
     * See also isDerivedFromSampling()
     * @returns {boolean}
     */
    isASample() {
        return Boolean( this.population && this.population.isASample());
    }

    isADistribution() {
        return Boolean( this.population && this.population.isADistribution());
    }

    /**
     * Is this attribute derived from the original attribute, in sampling mode?
     * e.g. true for when viewing a Distribution, viewing a Sample, *AND* when
     * viewing the Population but still in Sampling mode.
     * @returns {boolean}
     */
    isDerivedFromSampling() {
        return Boolean( this.population ); // true if we have a non-null saved population
    }

    /**
     * Save the current attribute's data as a population.
     * See also updateSampleCases(), restorePopulationData()
     * @param caseSampler
     */
    savePopulationData( caseSampler ) {
        Dyn.assert( ! this.isDerivedFromSampling(), "restore the population before making samples" );
        // Ignores repeat calls, the population data only needs saving once until restored
        if( ! this.isDerivedFromSampling()) {
            this.population = new Dyn.PopulationAttribute( this, caseSampler );
            // save and change our .originalCases, .cases, .statsPreFilter, .stats to make a sample
            this.population.savePopulationData( this, caseSampler );
        }
        Dyn.assert( this.isDerivedFromSampling() );
    }

    /**
     * Update the attribute's sample data to match the cases of our dataSet's case sampler.
     * @param caseSampler
     */
    updateSampleCases( caseSampler, selectedView ) {
        Dyn.assert( this.isDerivedFromSampling(), "no population exists" );
        if( this.isDerivedFromSampling()) {
            // update our .originalCases, .cases, .statsPreFilter, .stats to show the new sample
            if( selectedView==='viewDistribution') {
                this.population.updateCasesToDistribution(this, caseSampler);
            } else if ( selectedView==='viewPopulation') {
                this.population.updateCasesToPopulation(this, caseSampler);
            } else {
                this.population.updateCasesToNthSample(this, caseSampler);
            }
        }
    }

    /**
     * Restore sample data to match the cases of our dataSet's case sampler.
     * See also savePopulationData(), updateSampleCases()
     * @param caseSampler
     */
    restorePopulationData( caseSampler ) {
        if( this.isDerivedFromSampling()) {
            this.population.restorePopulationData( this, caseSampler );
            delete this.population;
            this.population = null;
            // .originalCases, .cases, .stats, .statsPreFilter restored, should be nothing more to do
        }
        // else nothing to do
        Dyn.assert( ! this.isDerivedFromSampling() );
    }

    /**
     * Does this attribute have note string data directly playable by Dyn.sound sonifications?
     * Must have no invalid data to be true, missing cases are OK, see isToneJsNoteString()
     */
    hasNoteStringData() {
        if( ! (this.format.hasNoteStringData === false || this.format.hasNoteStringData === true )) {
            // lazy evaluation of whether we have note like 'A4' or not, by scanning case values
            let validNoteCount = 0,
                invalidNoteCount = 0,
                blankNoteCount = 0;
            this.originalCases.forEach( (caseValue) => {
                if( Dyn.sound.isToneJsNoteString( caseValue )) {
                    ++validNoteCount;
                } else if( Dyn.dataUtil.isMissing( caseValue )) {
                    ++blankNoteCount;
                } else {
                    ++invalidNoteCount;
                }
            });
            // change from undefined to true or false
            this.format.hasNoteStringData = Boolean( validNoteCount > 0 && invalidNoteCount < 1 );
        }
        return this.format.hasNoteStringData;
    }

    static unitTest() {
        Dyn.assert( Dyn.dataSet );
        let unitTester = new Dyn.UnitTester( "Dyn.Attribute.unitTest()" ),
            attribCaseArray = ["TestCat", 1, 2.3, 1.23456, "cat2" ],
            attribMetaData = {},
            attr = new Attribute( "testAttr", attribCaseArray, 0, 1, "testAttr_id", attribMetaData );

        attr.stats.categories.forEach((cat) =>
            numericCatIsFormattedProperly( unitTester, attr, cat ));
        unitTester.report();

        function numericCatIsFormattedProperly( unitTester, attr, cat ) {
            let catFormatted = attr.categoryValueToString( cat );
            if( Number.isFinite(Number(cat))) {
                unitTester.test( cat===catFormatted, "Numeric category format error for:"+cat);
            }
        }
    }

} // class Attribute
Dyn.Attribute = Attribute;
Dyn.debugUnitTestHasRun_Attribute = false;
setTimeout( () => { // delay the test until Dyn.dataSet is created
    if( Dyn.debugging ) {
        Dyn.debugUnitTestHasRun_Attribute = Attribute.unitTest();
    }
}, 200 );

class CombinedAttributes {

    /**
     * Constructor for CombinedAttributes class, used for an axis or statistic on multiple attributes
     * which are combined in various standard ways (e.g. plot two attributes on the same numeric axis)
     * @param iID
     */
    constructor( iID ) {
        this.name			= "";
        this.attributes		= [];
        this.cases			= [];
        this.stats			= null; // Dyn.dataUtil.calcStats( attribCaseArray );
        this.populationStats= null; // population stats if this.attributes[].isDerivedFromSampling()\
        this.statsPending   = false; // if true, .stats unreliable, may be changed soon as part of plot update cycle.
        this.id				= iID;	// reference ID given by caller
        this.numActive 		= 0;    // to identify attributes added but not used, ranges is [0 - attributes.length]
        this.isSortAscending = true;  // {Boolean} numeric/categorical sort order preference, defaults to ascending
        this.type			= 0;    //  0=null, 1=numeric, 2=categorical
        this.subType		= undefined;	// {string in Dyn.dataUtil.subtypes|undefined}
        this.dualAttributes = null; // reference to 2nd set of combined attributes, if any
    }


    setDualAttributes( secondCombinedAttributes ) {
        this.dualAttributes = secondCombinedAttributes; // may be null
        Dyn.assert( this.dualAttributes === null || // OK if null, this is not the 1st of dual attributes set
            this.dualAttributes.dualAttributes === null ); // or if non-null, no nesting or cross-references allowed.
    }

	count() {
		"use strict";
		return this.attributes.length;
	}

	/**
	 * How many of these attributes are active, not dimmed and unused?
	 * The active attributes are always in the start of the array.
	 * @returns {number}
	 */
	countActive() {
		"use strict";
		Dyn.assert( 0 <= this.numActive && this.numActive <= this.attributes.length );
        this.checkStatsPending();
		return this.numActive;
	}

    countActiveIncludingDualAxis() {
        this.checkStatsPending();
	    return this.numActive + (this.dualAttributes ? this.dualAttributes.numActive : 0);
    }

	/**
	 * Set how many of these attributes are active.
	 * The active attributes are always in the start of the array.
	 * If there not-active attributes, they should not be used in plot, and dimmed on the axis display.
	 * @param numActiveAttributes
	 */
	setNumActive( numActiveAttributes ) {
		"use strict";
		this.numActive = Dyn.mathUtil.clipToIntegerRange( numActiveAttributes, 0, this.attributes.length + 1 );
		Dyn.assert( this.numActive === numActiveAttributes, "invalid numActiveAttributes" ); // warn if we had to clip the value
		this.updateStats();
	}

    checkStatsPending() {
        Dyn.assert( ! this.statsPending, "numActive and type not reliable, do not use at this stage of plot update.");
    }

    /**
     * @param wantPending {boolean}
     */
    setStatsPending( wantPending ) {
        // set state flag to warn callers that plot is being updated,
        // this.numActive is not yet reliable.
        // setNumActive() clear this flag.
        this.statsPending = wantPending;
    }

	/**
	 * add an attribute to our list.
     * NOTE: Caller (e.g. axisView) is responsible for notifying views that attributes have changed
	 */
	addAttribute( newAttr ) {
		"use strict";
		var success = false;
		Dyn.assert( newAttr === null || newAttr instanceof Dyn.Attribute );
		if( newAttr !== null && this.attributes.indexOf( newAttr ) === -1 ) {
			// not a duplicate, OK to add it to the array.
			this.attributes.push( newAttr );
			this.numActive += 1; // by default we increase the number of active attributes to include this one.
			this.updateStats();
			success = true;
		}
		return success;
	}

	/**
	 * remove one attribute
     * NOTE: Caller (e.g. axisView) is responsible for notifying views that attributes have changed
	 * @param oldAttrObjectOrIndex {Dyn.Attribute|number} -- reference to object or index in array
	 * @returns {boolean}
	 */
	removeAttribute( oldAttrObjectOrIndex ) {
		"use strict";
		var success = false,
			isIndex = Dyn.dataUtil.isNumber( oldAttrObjectOrIndex ),
			index = (isIndex ? oldAttrObjectOrIndex : this.attributes.indexOf( oldAttrObjectOrIndex ));
		if( isIndex ) {
			Dyn.assertArrayIndex( this.attributes, oldAttrObjectOrIndex );
		} else {
			Dyn.assert( oldAttrObjectOrIndex instanceof Dyn.Attribute );
		}
		if( index !== -1 ) {
			// we found attribute in array, now remove it
			this.attributes.splice( index, 1 );
            this.numActive = Dyn.mathUtil.clipToIntegerRange( this.numActive, 0, this.attributes.length + 1 );
			this.updateStats();
			success = true;
		}
		return success;
	}

	/**
	 * @param attributeName - Dyn.Attribute or other value
	 * @returns {Dyn.Attribute|null} - returns attribute object or null
	 */
	findAttributeByName( attributeName ) {
		"use strict";
		var attr = null;
		$.each( this.attributes, function( index, value ){
			if( value.name === attributeName ) {
				attr = value;
			}
		});
		return attr;
	}

	findAttribute( otherAttribute ) {
		"use strict";
		// return index of attribute or -1
		return this.attributes.indexOf( otherAttribute );
	}

	/**
	 * When the attribute is dropped on the 1st drop area, clears
	 * all the attributes from the axis.
	 * NOTE: Caller (e.g. axisView) is responsible for notifying views that attributes have changed
	 */
	clearAttributes() {
		"use strict";
		// clear all references to our attribute objects
		this.attributes.length = 0;
		this.numActive = 0;
		this.updateStats();
	}

	getJoinedTitles( separatorString ) {
		"use strict";
		var titleString ="";
		$.each( this.attributes, function( index, value ){
			titleString += ((index>0)?separatorString:"") + value.name;
		});
		return titleString;
	}

	getJoinedCases() {
		"use strict";
		var caseArrays = [],
			joinedCases = [],
			activeAttrIndex = this.numActive;
		// get a reference to the cases[] array for all active attributes
		$.each( this.attributes, function( index, value ){
			if( index <= activeAttrIndex ) {
				caseArrays.push( value.cases );
			}
		});
		// flatten the arrays into one long value array
		joinedCases = joinedCases.concat.apply( [], caseArrays );
		return joinedCases;
	}

	/**
	 * Get the overall axis type
	 * Decides the default type if there are multiple attributes of different types on the axis.
	 * @returns {number} - enumeration 0=null, 1=numeric, 2=categorical
	 */
	getAxisType() {
		"use strict";
		// get the axis type as a enumeration 0=null, 1=numeric, 2=categorical
		var hasNumericAttribute = false,
			hasCategoricalAttribute = false,
			type = 0;

		this.attributes.forEach( (attr, index) => {
            if( index < this.numActive ) {
                if( attr.getDataType() === Dyn.dataUtil.types.kNumerical ) {
                    hasNumericAttribute = true;
                } else {
                    hasCategoricalAttribute = true;
                }
            }
		});

		if( hasCategoricalAttribute ) {
			type = 2; // 1 or more categorical attributes, or mix.
		} else if ( hasNumericAttribute ) {
			type = 1; // 1+ numeric attributes, no categorical attributes
		}
		// else no attributes = type 0
		return type;
	}

	/**
	 * Determine the subtype from the type and the stats
	 * @returns {undefined|string} -- undefined or enumeration Dyn.dataUtil.subtypes
	 */
	getAxisSubType() {
		"use strict";
		var subtype; // default is undefined subtype;
		if( this.type === 1 && this.numActive > 0 ) {
            // else use subtype of 1st attribute
			subtype = this.attributes[0].subType;
		}
		return subtype;
	}

	/** Update the derived statistics; do once when attribute added/removed. */
	updateStats() {
		"use strict";
		// silently correct the number of active attributes, only if out of range, for example if we removed one attribute
		this.numActive = Dyn.mathUtil.clipToIntegerRange( this.numActive, 0, this.attributes.length + 1 );

		this.name = this.getJoinedTitles("\n");
		this.cases = this.getJoinedCases();
        this.stats = new Dyn.dataUtil.AttributeStats(); // create a new stats object
        this.populationStats = (this.numActive >= 1 && this.attributes[0].isDerivedFromSampling() ) ? new Dyn.dataUtil.AttributeStats() : null;
        this.attributes.forEach( (attribute, index ) => {
            // merge in the stats of each active attribute
            if( index < this.numActive ) {
                attribute.updateStatsIfDataChange();
                this.stats.mergeStats( attribute.stats );
                if( this.isDerivedFromSampling()) { // true if this.populationStats !== null
                    this.populationStats.mergeStats( attribute.population.stats );
                }
                Dyn.assert( this.isDerivedFromSampling() === attribute.isDerivedFromSampling(), "attributes should all be samples or all be populations, instead we have a mix");
            }
        });
		// calculate type and subtype AFTER stats are calculated
		this.type = this.getAxisType();  		//  0=null, 1=numeric, 2=categorical
		this.subType = this.getAxisSubType();	//	{string in Dyn.dataUtil.subtypes|undefined}
		// for now use 1st attribute sort preference for multiple attributes, we may want more user control in the future.
		this.isSortAscending = ( this.numActive >= 1 ? this.attributes[0].isSortAscending : true )
        this.setStatsPending( false );
        //console.log( "CombinedAttributes.updateStats()", "type", this.type, "numActive", this.numActive, this.name );
	}

	/**
	 * Check to see if the Dyn.DataSet filter/missing value state has changed since we last calculated stats.
	 * If changed, recalculate the statistics to change the list of categories, numeric range, etc.
	 */
	updateStatsIfDataChange() {
		"use strict";
		var oldStatsFilterID = this.stats ? this.stats.filterID : 0,
			newStatsFilterID = Dyn.dataSet.getStatsFilterID(),
			tChanged = false;

		if( oldStatsFilterID !== newStatsFilterID ) {
			this.updateStats();
			tChanged = true;
		}
		return tChanged;
	}

    /**
     * Is this combined attribute created from samples of the original attributes?
     * @returns {boolean}
     */
    isASample() {
        return Boolean( this.attributes.length && this.attributes[0].isASample());
    }

    /**
     * Is this combined attribute created from a distribution of samples of the original attributes?
     * @returns {boolean}
     */
    isADistribution() {
        return Boolean( this.attributes.length && this.attributes[0].isADistribution());
    }

    /** is this combined attribute created from attributes with isDerivedFromSampling() true? */
    isDerivedFromSampling() {
        let hasPopStats = Boolean( this.populationStats ), // true if we have a non-null saved population stats
            hasDerivedAttr = Boolean( this.attributes.length && this.attributes[0].isDerivedFromSampling());
        Dyn.assert( hasPopStats === hasDerivedAttr );
        return hasDerivedAttr;
    }

    /**
     * Get the number of decimals (0+) to display, to round numeric values to a certain number of significant digits.
     * Should be similar to Dyn.Attribute.getNumericDecimals, but taking all attributes into account
     */
    getNumericDecimals( optionalDecimals, ignoreUserOverride ) {
        if( this.numActive > 0 ) {
            // possible future enhancement: if multiple attributes, take them all into account
            return this.attributes[0].getNumericDecimals( optionalDecimals, ignoreUserOverride );
        }
        return optionalDecimals || 0;
    }

    /** get the string equivalent of the values, using attribute formatting, if any */
    valueToString( value, optionalDecimals ) {
        if( this.numActive > 0 ) {
            // Possible future enhancement: if multiple attributes, take them all into account
            return this.attributes[0].valueToString( value, optionalDecimals );
        } else {
            // if no attribute, we may be getting values of a frequency attribute
            return value.toString();
        }
    }
    categoryValueToString( value ) {
        if( this.numActive > 0 ) {
            // Possible future enhancement: if multiple attributes, take them all into account
            return this.attributes[0].categoryValueToString( value );
        } else {
            // if no attribute, we may be getting values of a frequency attribute
            return value.toString();
        }
    }

    /**
     *  Get the attribute (default to 1st attribute), returning null if it does not exist
     */
    getActiveAttribute( attributeIndex ) {
        let attribute = this.attributes[ attributeIndex ];
        if( this.dualAttributes && (attributeIndex >= this.numActive)) {
            attribute = this.dualAttributes.attributes[ attributeIndex - this.numActive ];
        }
        return attribute || null;
    }

    isDualAttribute( attributeIndex ) {
        Dyn.assert( attributeIndex < this.countActiveIncludingDualAxis());
        return Boolean( this.dualAttributes && (attributeIndex >= this.numActive));
    }

    /**
     *  Get copy of our array of attributes.
     *  The default is to exclude inactive attributes and include dual attributes if any.
     */
    getAttributesArray( wantInactiveAttributes=false, ignoreDualAttributes=false ) {
        let attrs = [];
        this.attributes.forEach( (a, i) =>{
            if( wantInactiveAttributes || (i<this.numActive)) {
                attrs.push( a );
            }
        });
        if( this.dualAttributes && ! ignoreDualAttributes ) {
            this.dualAttributes.attributes.forEach( (a, i) =>{
                if( wantInactiveAttributes || (i<this.dualAttributes.numActive)) {
                    attrs.push( a );
                }
            });
        }
        return attrs;
    }

    /**
     * Get the axis data range for the combined attributes, and if in sampling mode,
     * modify that range as follows:
     *  If attributes are samples in sampling mode, use the range of the population,
     *      so that samples and population are easily compared (sample range will be
     *      within the population range).
     *  If attributes are distributions of samples in sampling mode, use the union
     *      of the population and distribution data range.  For ranges of distributions
     *      of sample IQR, sample st.dev, or sample Variance, the distribution data
     *      range may be outside the population's data range.
     * @returns {Range}
     */
    getModifiedDataRange() {
        let range;
        if( this.isASample()){ // use population range
            range = new Dyn.mathUtil.Range(this.populationStats.min, this.populationStats.max);
        } else if( this.isADistribution()){ // union of population and distribution range
            range = new Dyn.mathUtil.Range( this.populationStats.min, this.populationStats.max );
            range.addPoint( this.stats.min );
            range.addPoint( this.stats.max );
        } else {  // use ordinary data range
            range = new Dyn.mathUtil.Range(this.stats.min, this.stats.max);
        }
        return range;
    }

    /**
     * Get a user-specified axis range from attribute metaData, if it exists, so that authors can specify
     * a different default numeric axis range than what getModifiedDataRange() returns.
     * This default user range does not prevent the user to further modify it during plot use, if desired.
     */
    getDefaultUserRange() {
        if( this.attributes.length && !( this.isASample() || this.isADistribution()) ) {
            const format = this.attributes[0].format;
            if( isFinite( format.axisMin ) || isFinite( format.axisMax )) { // if one or both defined
                let range = new Dyn.mathUtil.Range( this.stats.min, this.stats.max );
                if( isFinite( format.axisMin ) ) {
                    range.min = format.axisMin; // set axis range minimum
                }
                if( isFinite( format.axisMax ) ) {
                    range.max = format.axisMax; // set axis range minimum
                }
                return range;
            }
        }
        return null;
    }

} // class CombinedAttributes
Dyn.CombinedAttributes = CombinedAttributes;


class PopulationAttribute {

    /**
     * PopulationAttribute saves the data of an attribute which is about to be sampled,
     * and is responsible for the modification (e.g. Population -> Sample 3), and restoration of the
     * original (population) data.
     *
     * Population -- the original attribute data, saved
     * Sample -- the modified attribute, which has a new number of cases (the sampled cases of the population)
     * Distribution -- the modified attribute with cases replaced by a statistical summary
     *      (e.g. means of N samples of the original attribute)
     *
     * NOTE: The parameters we save here are those used to restore the original population after closing
     * the sampling state/sampling mode.  This is an "in memory" save, and is not intended to persist longer than
     * that (e.g. between sessions).
     *
     * @param originalAttribute
     * @param caseSampler
     * @constructor
     */
    constructor( originalAttribute, caseSampler ) {
        // the following parameters are modified when generating or viewing a sample or distribution of samples,
        // therefore they need to be saved on Generate, so they can be restored after exiting sampling mode.
        if( caseSampler.allowChangeToAttributeNames ) {
            this.name = '';
            this.longName = '';
        }
        this.originalCases = [];    // the original cases of the population
        this.cases = [];            // the filtered, type-changed cases of the population
        this.statsPreFilter = null; // {AttributeStats} of population before filtering
        this.stats = null;          // {AttributeStats} of population after filtering
        this.numericDecimals = 0;   // original attribute.format.decimals, to restore after fixPrecsion()
        this.isVisible = true;      // isVisible flag of population (modified by distribution)
        this.origAttrState = '';    // what is the state of the original attribute, which we modify?
    }

    /** true if the original attribute was modified to be a sample; see updateCasesToNthSample() */
    isASample() {
        return this.origAttrState==='isASample';
    }

    /** true if the original attribute was modified to be a distribution; see updateCasesToDistribution() */
    isADistribution() {
        return this.origAttrState==='isADistribution';
    }

    /** save or restore .originalCases and .cases for matching Dyn.Attribute */
    savePopulationData( originalAttribute, caseSampler ) {
        // save the original attribute name
        if( caseSampler.allowChangeToAttributeNames ) {
            this.name = originalAttribute.name;
            this.longName = originalAttribute.longName;
        }

        // save the original attributes case data, it is now the population
        this.originalCases              = originalAttribute.originalCases;
        this.cases                      = originalAttribute.cases;

        // save population filters and clear the sample attribute's filters
        this.statsPreFilter = originalAttribute.statsPreFilter;
        this.stats = originalAttribute.stats;
        this.numericDecimals = originalAttribute.format.decimals;
        this.isVisible = originalAttribute.isVisible;
        this.origAttrState = 'saved';

        // for calculating precision of cases by callers of updateCasesToDistribution
        this.origNumericPrecision = originalAttribute.getNumericDecimals();
    }

    restorePopulationData( originalAttribute, caseSampler ) {
        // restore the population case data
        if( caseSampler.allowChangeToAttributeNames ) {
            originalAttribute.name = this.name;
            originalAttribute.longName = this.longName;
        }
        originalAttribute.originalCases = this.originalCases;
        originalAttribute.cases         = this.cases;
        this.originalCases = [];
        this.cases = [];

        // restore original filters
        originalAttribute.statsPreFilter = this.statsPreFilter;
        originalAttribute.stats = this.stats;
        originalAttribute.format.decimals = this.numericDecimals;
        originalAttribute.isVisible = this.isVisible;
        this.statsPreFilter = null;
        this.stats = null;
        this.origAttrState = 'restored';
    }

    /**
     * Update originalAttribute's cases and filters to make a sample,
     *  for temporarily viewing one of many samples while in Sampling mode.
     * Does not change this PopulationAttribute.
     */
    updateCasesToNthSample( originalAttribute, caseSampler ) {
        Dyn.assert( originalAttribute.isDerivedFromSampling(), "missing population restoration data");
        if( caseSampler.allowChangeToAttributeNames ){
            originalAttribute.name = this.name;
            originalAttribute.longName = this.longName;
        }
        originalAttribute.originalCases = [];
        originalAttribute.cases         = [];
        originalAttribute.statsPreFilter = null;
        originalAttribute.stats = null;
        originalAttribute.format.decimals = this.numericDecimals;
        originalAttribute.isVisible = this.isVisible;

        // create a sample of the cases, save with original attribute
        originalAttribute.originalCases = caseSampler.sample.caseIndices.map(
            element => this.originalCases[element] );

        // re-create .cases, .statsPreFilter, .stats
        originalAttribute._calcDataTypeAndOrder();
        this.fixSampleColors( originalAttribute );

        this.origAttrState = 'isASample';  // record which state we are in
        Dyn.assert( originalAttribute.isASample());
    }

    /**
     * Update originalAttribute's cases and filters to the distribution values,
     *  for temporarily viewing the distribution while in Sampling mode.
     * Does not change this PopulationAttribute.
     */
    updateCasesToDistribution( originalAttribute, caseSampler ) {
        Dyn.assert( originalAttribute.isDerivedFromSampling(), "missing population restoration data");
        originalAttribute.originalCases = [];
        originalAttribute.cases         = [];
        originalAttribute.statsPreFilter = null;
        originalAttribute.stats = null;

        // update case values with matching distribution values
        let attrData = caseSampler.findDistributionAttributeData( originalAttribute.id );
        if( attrData ){ // replace cases with distribution values
            originalAttribute.originalCases = Array.from(attrData.cases);
            if( caseSampler.allowChangeToAttributeNames ) {
                originalAttribute.setName(attrData.name);
                originalAttribute.setLongName(attrData.longName);
            }
            originalAttribute.isVisible = this.isVisible;
        } else {         // else replace cases with missing values
            let numCases = caseSampler.getNumCasesInDistribution();
            originalAttribute.originalCases = Dyn.dataUtil.filledArray( numCases, Dyn.dataUtil.kMissingValue );
            originalAttribute.isVisible = false; // hide attribute in data table
        }

        // re-create .cases, .statsPreFilter, .stats
        originalAttribute._calcDataTypeAndOrder();
        this.fixPrecision( originalAttribute, attrData && attrData.precision );
        this.fixSampleColors( originalAttribute );

        this.origAttrState = 'isADistribution'; // record which state we are in
        Dyn.assert( originalAttribute.isADistribution() && ! originalAttribute.isASample());
    }

    /**
     * Update originalAttribute's cases and filters to the population values,
     *  for temporarily viewing the Population while in Sampling mode.
     *  (unlike originalAttribute.restorePopulationData() which restores the
     *   attribute changes made in Sampling mode).
     * Does not change this PopulationAttribute.
     */
    updateCasesToPopulation( originalAttribute, caseSampler ) {
        Dyn.assert( originalAttribute.isDerivedFromSampling(), "missing population restoration data");
        if( caseSampler.allowChangeToAttributeNames ){
            originalAttribute.name = this.name;
            originalAttribute.longName = this.longName;
        }
        originalAttribute.originalCases = [];
        originalAttribute.cases         = [];
        originalAttribute.statsPreFilter = null;
        originalAttribute.stats = null;
        originalAttribute.format.decimals = this.numericDecimals;
        originalAttribute.isVisible = this.isVisible;

        // restore the original population cases
        originalAttribute.originalCases = Array.from( this.originalCases );

        // re-create .cases, .statsPreFilter, .stats
        originalAttribute._calcDataTypeAndOrder();
        this.fixSampleColors( originalAttribute );

        this.origAttrState = 'isAPopulation';  // record which state we are in
        Dyn.assert( ! originalAttribute.isASample());
        Dyn.assert( originalAttribute.isDerivedFromSampling()); // still in sampling mode
    }

    /**
     * Fix the data used to determine the dynamic category colors.
     * @param originalAttribute
     */
    fixSampleColors( originalAttribute ) {
        // colors are determined by the statsPreFilter, which until now are not "pre-Sampling"
        // here we update the numeric range and categories so that the dynamic colors use the full range
        // or full number of categories in the original population.
        //
        // For example if gender male is green in the population, it should stay green in the sample also.
        // otherwise for dynamic colors, if certain categories are filtered out, or sampled out,
        // the other colors change because the number of categories has changed.
        //
        // NOTE: numeric color range does not seem to use statsPreFilter, not sure why, maybe this should be changed,
        // so that both sample colors and post-filter numeric colors are the same as the pre-filter, population colors? --CDM 2019-06-04
        originalAttribute.statsPreFilter.min =          this.statsPreFilter.min;
        originalAttribute.statsPreFilter.max =          this.statsPreFilter.max;
        originalAttribute.statsPreFilter.categories =   this.statsPreFilter.categories;
    }

    /**
     * fix the display precision, if we updated cases to stat of cases (e.g. weight to mean weight),
     * for UpdateCasesToDistribution.()
     * But it is needed here only because case values are calculated and thus usually many decimal places long.
     * @param originalAttribute
     * @param precision
     */
    fixPrecision( originalAttribute, precision ) {
        if( Dyn.mathUtil.isFinite( precision )) {
            originalAttribute.setNumericDecimals( precision );
            //console.log( "PopulationAttribute:fixPrecision", originalAttribute.getNumericDecimals() );
        }
    }

} // classs PopulationAttribute
Dyn.PopulationAttribute = PopulationAttribute;

} // wrapDynAttribute
